import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import {NgbActiveModal, NgbModal, NgbModalRef,} from "@ng-bootstrap/ng-bootstrap";
import {BehaviorSubject, catchError, forkJoin, map, mergeMap, Observable, of, shareReplay, Subject, Subscription, takeUntil, tap, throwError,} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Router} from "@angular/router";
import {ErrorComponent} from "src/app/error/error.component";

import {environment} from 'src/environments/environment';
import {OwpPasswordComponent} from "src/app/owp-password/owp-password.component";
import {FEATURE_NORMALIZATION, FEATURE_PATTERNS} from "../../constants/supported-features-constant";
import {setResultFeatures} from "../corruptfeatures/corrupt-features-processing";
import {AppStateService} from "src/app/core-module/services/app-state.service";
import { AvailableFeaturesStateService } from "../../../core-module/services/state/available-features-state.service";
import { CorruptFeaturesService } from "../../../core-module/services/state/corrupt-features.service";
import { DeviceConfigStateService } from "../../../core-module/services/state/device-config-state.service";
import { DimmingInterfaceStateService } from "../../../core-module/services/state/dimming-interface-state.service";
import { FeatureUtilsService } from "../../../core-module/services/state/feature-utils.service";
import { MultiOneHttpClient } from "../../../core-module/services/state/multi-one-http-client.service";
import { OwpStateService } from "../../../core-module/services/state/owp-state.service";
import { UiSignalsStateService } from "../../../core-module/services/state/ui-signals-state.service";
import {
  DEFAULT_DEVICE_REGIONS,
  DEVICE_SEARCH_ENDPOINT,
} from "src/app/shared-module/constants/configure-device-modal.constants";
import {
  CORRUPT_BIT_URL,
  DEVICE_INFO_URL,
  EXTENDED_MEMORY_BANKS,
  FORMATTED_MEMORY_BANK_URL,
  IMPORT_PAYLOAD_URL,
  IMPORT_PAYLOAD_URL_NE,
  MEMORY_BANK_URL,
  PAYLOAD_URL,
  READER_BASE_URL,
  READER_DATA_URL,
  READER_LIST_URL,
  READER_START_URL,
  WRITING_IN_DEVICE_URL,
} from "src/app/shared-module/constants/reader-device.constants";
import { FEATURE_SELECTION_CANONICAL_MAP } from "src/app/shared-module/constants/feature-supported.constants";
import {
  FEATURE_LABEL_AMP_DIM,
  FEATURE_LABEL_AOC_MULTI_CHANNEL,
  FEATURE_LABEL_AUX_OUTPUT_VOLTAGE,
  FEATURE_LABEL_CLO_LITE,
  FEATURE_LABEL_DYNA_DIMMER,
  FEATURE_LABEL_LINE_SWITCH,
  FEATURE_LABEL_MIN_DIM_LEVEL,
  FEATURE_LABEL_STARTUP_TIME,
  FEATURE_LABEL_TOUCH_AND_DIM,
  FEATURE_LABEL_ZTV,
  FEATURE_LABEL_LOW_MAINS_LOCK,
  FEATURE_LABEL_DIPSWITCH
} from "src/app/shared-module/constants/feature-supported.constants";

const READER_NOT_CONNECTED_MSG = "Reader not connected";
const DEVICE_OR_PERIPHERAL_RETRY_HINT = "Please check the Device or Peripheral ID and try again!";

const CONFIGURATION_VERSION_FIELD_SOURCES: Readonly<Record<string, string>> = {
  luminSoftVer0: "lumin0SoftwareVersionSchema",
  luminSoftVer3: "lumin3SoftwareVersionSchema",
  luminSoftVer4: "lumin4SoftwareVersionSchema",
  luminSoftVer5: "lumin5SoftwareVersionSchema",
  dtlSoftVer: "dtlSoftwareVersionSchema",
  flextuneSoftVer: "flextuneSoftwareVersionSchema",
  mindimlevelSoftVer: "mindimlevelSoftwareVersionSchema",
  ztvSoftVer: "ztvSoftwareVersionSchema",
  ztvoutputSoftVer: "ztvoutputSoftwareVersionSchema",
  lipSoftVer: "lipSoftwareVersionSchema",
  rsoSoftVer: "rsoSoftwareVersionSchema",
  aocSoftVer: "aocSoftwareVersionSchema",
  touchdimSoftVer: "touchdimSoftwareVersionSchema",
  diia253maintenanceSoftVer: "diia253maintenanceSoftwareVersionSchema",
  corridorSoftVer: "corridorSoftwareVersionSchema",
  astSoftVer: "astSoftwareVersionSchema",
  cloliteSoftVer: "cloliteSoftwareVersionSchema",
  cloSoftVer: "cloSoftwareVersionSchema",
  multiconfigSoftVer: "multiconfigSoftwareVersionSchema",
  mtpSoftVer: "mtpSoftwareVersionSchema",
  dynadimmerSoftVer: "dynadimSoftwareVersionSchema",
  dcemergencySoftVer: "dcemergencySoftwareVersionSchema",
  ampdimSoftVer: "ampdimSoftwareVersionSchema",
  lineswitchSoftVer: "lineswitchSoftwareVersionSchema",
  owpSoftVer: "owpSoftwareVersionSchema",
  codedmainsSoftVer: "codedmainsSoftwareVersionSchema",
  dalipsuSoftVer: "dalipsuSoftwareVersionSchema",
  dimminginterfaceSoftVer: "dimminginterfaceSoftwareVersionSchema",
  dali102AddressingSoftVer: "daliAddressing102SoftwareVersionSchema",
  dali102and207ConfigSoftVer: "daliConfiguration102and207SoftwareVersionSchema",
  auxSoftVer: "auxSoftwareVersionSchema",
  lsiSoftVer: "lsiSoftwareVersionSchema",
  aocmultichannelSoftVer: "aocmultichannelSoftwareVersionSchema",
  otpSoftVer: "otpSoftwareVersionSchema",
  dscSoftVer: "dscSoftwareVersionSchema",
  lowmainslockSoftVer: "lowmainslockSoftwareVersionSchema",
  dipswitchSoftVer: "dipswitchSoftwareVersionSchema"
};

interface FeatureWithParameters {
  featureParameters?: unknown;
}

interface ApiErrorLike {
  status?: string | number;
  message?: string;
  Message?: string;
  description?: string;
}

interface MemoryBankReference {
  memoryBankId: string;
}

interface FormattedMemoryProperty {
  memoryBankId?: string;
  memoryBankName?: string;
  parameters?: Record<string, number | boolean | string | undefined>;
  [key: string]: unknown;
}

interface FormattedMemoryResponse {
  formattedProperties: FormattedMemoryProperty[];
  iv?: unknown;
  metadata?: unknown;
  owp?: unknown;
  [key: string]: unknown;
}

interface PwmWriteResult {
  pwmDevice?: {
    readMemoryBanks?: MemoryBankReference[];
    writeMemoryBanks?: FormattedMemoryProperty[];
    aocCurrent2PwmConversionTable?: Array<{ aocCurrent: number; pulseWidth: number }>;
  };
  [key: string]: unknown;
}

const UNIQUE_CONFIG_ID_PLACEHOLDER = "NA";

@Component({
  standalone: false,
  selector: "app-read-device",
  templateUrl: "./read-device.component.html",
  styleUrls: ["./read-device.component.scss"],
})
export class ReadDeviceComponent implements OnInit, OnDestroy {
  @Input() public newPayLoadValues: unknown;
  @Input() public isRetry: boolean = false;
  readDevice: unknown;
  deviceNfcId: unknown;
  showData: boolean = false;
  readingStatus: unknown;
  private readonly loadingSignal = signal(false);
  readerData: LegacyAny;
  corruptbitfailed: boolean = false;
  selectedReader: Record<string, unknown>;
  availableFeatures: string[] = [];
  private readonly hideStatusSignal = signal(false);
  private readonly customerLoaderSignal = signal(false);
  isProductionInfoAvailable: boolean = false;
  private readonly customerLoaderAfterSignal = signal(false);
  isReadFlowActive: boolean = false;  // Track if we're actively reading device data
  private readonly noReaderSignal = signal(false);
  specialErrorNoDatabase: boolean = false;
  baseLinkUrl = READER_BASE_URL;
  readerStartUrl = READER_START_URL;
  readerListUrl = READER_LIST_URL;
  readreDataUrl = READER_DATA_URL;
  readerName: LegacyAny;
  listReader: unknown[] = [];
  errorMessage: string;
  iscorrupt: boolean = false;
  private readonly readerPresenceSignal = signal(false);
  readerLength: unknown[] = [];
  deviceInfoUrl = DEVICE_INFO_URL;
  memoryBankUrl = MEMORY_BANK_URL;
  envScehmaLIstApi: string;
  corruptBitUrl = CORRUPT_BIT_URL;
  writingInDeviceURL = WRITING_IN_DEVICE_URL;
  formattedMemoryBankUrl = FORMATTED_MEMORY_BANK_URL;
  listOfBank: unknown;
  unsubscribe = new Subject<void>();
  allFeatures: string[];
  feature12NcName: string[] = [];
  readerBaseUrl: string;
  readerDeviceData: LegacyAny;
  featureWithId: LegacyAny[];
  productionDate: unknown;
  productionBatchId: unknown;
  sku: unknown;
  nonCorrupt = [];
  corrupt = [];
  payLoadUrl = PAYLOAD_URL;
  importPayLoadUrl = IMPORT_PAYLOAD_URL;
  importPayLoadUrlNE = IMPORT_PAYLOAD_URL_NE;
  startConfiguration: LegacyAny;
  specificFeaturePayLoad: LegacyAny;
  url: string;
  deviceInformationObj: Record<string, unknown> = {};
  bankIdByIndex: LegacyAny;
  newDeviceObj: unknown;
  envHostName: string;
  apiListResponse: unknown[] = [];
  mem: LegacyAny = [];
  softwareVersionSchema: string;
  lumin0SoftwareVersionSchema: string;
  lumin3SoftwareVersionSchema: string;
  lumin4SoftwareVersionSchema: string;
  lumin5SoftwareVersionSchema: string;
  everySchemaofDevice: unknown;
  alominBankId: boolean;
  isALoMinObj: boolean;
  isDalicongiuration102and207Obj: boolean;
  isOwpObj: boolean;

  private assignConfigurationVersionFields(): void {
    const componentState = this as unknown as Record<string, unknown>;

    Object.entries(CONFIGURATION_VERSION_FIELD_SOURCES).forEach(([configurationKey, sourceKey]) => {
      this.configurationData[configurationKey] = componentState[sourceKey];
    });
  }

  private normalizeSelectedFeatureValue(feature: string): string {
    const trimmedFeature = feature.trim();
    const normalizedFeature = trimmedFeature.toLowerCase();

    return FEATURE_SELECTION_CANONICAL_MAP[normalizedFeature] || trimmedFeature;
  }

  private appendSelectedFeature(selectedFeaturesInfo: string[], feature: string): void {
    const normalizedFeature = this.normalizeSelectedFeatureValue(feature);

    if (!selectedFeaturesInfo.includes(normalizedFeature)) {
      selectedFeaturesInfo.push(normalizedFeature);
    }
  }

  private buildFeatureConfigurationRequestPayload(properties: unknown): Record<string, unknown> {
    const memState = this.mem as Record<string, unknown>;
    const payload: Record<string, unknown> = {
      deviceName: memState.deviceName,
      device12nc: memState.device12nc,
      deviceSwVersion: memState.deviceSwVersion,
      interface: memState.interface,
      properties,
    };

    if (Object.prototype.hasOwnProperty.call(memState, "iv")) {
      payload.iv = memState.iv;
    }

    return payload;
  }
  private readonly isWritingRetryCheckSignal = signal(false);
  payLoadResp: unknown;
  subscription: Subscription;
  private readonly removeSuccessTextSignal = signal(false);
  private readonly writingDevicesContainerSignal = signal(false);
  writingURL: string;
  private readonly removeWritingHeaderSignal = signal(true);
  writingURLViaPlugin =
    "feature/api/v1/configuration/{configurationId}/feature-payload";
  writingURLViaPluginNE =
    "feature/api/v1/configuration/{configurationId}/feature-payload/not-encrypted";
  isAocMinObj: boolean;
  isLumMinSixObj: boolean;
  get loading(): boolean { return this.loadingSignal(); }
  set loading(value: boolean) { this.loadingSignal.set(!!value); }

  get hideStatus(): boolean { return this.hideStatusSignal(); }
  set hideStatus(value: boolean) { this.hideStatusSignal.set(!!value); }

  get customerLoader(): boolean { return this.customerLoaderSignal(); }
  set customerLoader(value: boolean) { this.customerLoaderSignal.set(!!value); }

  get customerLoaderAfter(): boolean { return this.customerLoaderAfterSignal(); }
  set customerLoaderAfter(value: boolean) { this.customerLoaderAfterSignal.set(!!value); }

  get noReader(): boolean { return this.noReaderSignal(); }
  set noReader(value: boolean) { this.noReaderSignal.set(!!value); }

  get readerPresence(): boolean { return this.readerPresenceSignal(); }
  set readerPresence(value: boolean) { this.readerPresenceSignal.set(!!value); }

  get isWritingRetryCheck(): boolean { return this.isWritingRetryCheckSignal(); }
  set isWritingRetryCheck(value: boolean) { this.isWritingRetryCheckSignal.set(!!value); }

  get removeSuccessText(): boolean { return this.removeSuccessTextSignal(); }
  set removeSuccessText(value: boolean) { this.removeSuccessTextSignal.set(!!value); }

  get writingDevicesContainer(): boolean { return this.writingDevicesContainerSignal(); }
  set writingDevicesContainer(value: boolean) { this.writingDevicesContainerSignal.set(!!value); }

  get removeWritingHeader(): boolean { return this.removeWritingHeaderSignal(); }
  set removeWritingHeader(value: boolean) { this.removeWritingHeaderSignal.set(!!value); }

  specificMemoryBanksList = [...EXTENDED_MEMORY_BANKS];
  regions: string[];
  searchName: unknown[];
  apiList: unknown[] = [];
  deviceInfo: LegacyAny;
  fullDeviceList = DEVICE_SEARCH_ENDPOINT;
  envDeviceListURL: string;
  writingDevice: boolean;
  corruptBits: LegacyAny;
  isNormalFlow: boolean;
  dtlSoftwareVersionSchema: string;
  dalipsuSoftwareVersionSchema: string;
  startConfigurationURL: LegacyAny;
  allMemoryBanks: { memoryBankId: string } = { memoryBankId: "All" };
  envAloschemaDetails: string;
  configurationData: Record<string, unknown>;
  latestDeviceVersion: string[] = [];
  latestDvValue: string;
  retryButton: boolean = false;
  retryClicked: boolean = false;
  mtpSoftwareVersionSchema: string;
  dimminginterfaceSoftwareVersionSchema: string;
  flextuneSoftwareVersionSchema: string;
  multiconfigSoftwareVersionSchema: string;
  cloSoftwareVersionSchema: string;
  aloSoftwareVersionSchema: string;
  aocSoftwareVersionSchema: string;
  touchdimSoftwareVersionSchema: string;
  diia253maintenanceSoftwareVersionSchema: string;
  corridorSoftwareVersionSchema: string;
  ztvSoftwareVersionSchema: string;
  ztvoutputSoftwareVersionSchema: string;
  lipSoftwareVersionSchema: string;
  rsoSoftwareVersionSchema: string;
  ampdimSoftwareVersionSchema: string;
  astSoftwareVersionSchema: string;
  cloliteSoftwareVersionSchema: string;
  dynadimSoftwareVersionSchema: string;
  dcemergencySoftwareVersionSchema: string;
  lineswitchSoftwareVersionSchema: string;
  mindimlevelSoftwareVersionSchema: string;
  owpSoftwareVersionSchema: string;
  codedmainsSoftwareVersionSchema: string;
  daliAddressing102SoftwareVersionSchema: string;
  daliConfiguration102and207SoftwareVersionSchema: string;
  auxSoftwareVersionSchema: string;
  lsiSoftwareVersionSchema: string;
  aocmultichannelSoftwareVersionSchema: string;
  otpSoftwareVersionSchema: string;
  dscSoftwareVersionSchema: string;
  lowmainslockSoftwareVersionSchema: string;
  dipswitchSoftwareVersionSchema: string;
  continueRead: boolean = false;
  isMultiDeviceChecked = false;
  deviceNfcIdsList:string[]=[];
  corruptedDevices: boolean = false;
  corruptedDevicesCount: number = 0;
  corruptedFeatureNames: string[] = [];
  newConfigurationID: string | null = null;

  errorCodedDC = [
    "206-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "304-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.1-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.2-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.3-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.4-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.5-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.6-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.7-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.8-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "400.9-Device not found! Please check the device and try again",
    "404.1- Please check the reader and try again",
    "404.2-No device or multiple devices found! Please check the device(s) and try again",
    "405-Device(s) have been changed or disconnected! \nPlease reconnect the original devices and try again",
    "412.1- Please check the reader and try again",
    "412.2-Device not found! Please check the device and try again",
    "422.2-Device(s) have been changed or disconnected! \nPlease reconnect the original devices and try again",
    "500-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
    "1203_1-Error occurred due to unsupported request! Please try again. If the issue persists contact Signify",
  ];
  hideFeatures = false;
  showExtraPopUp: boolean = true;
  showDeviceIDReadDC: boolean = false;
  showContniueNowTimer: ReturnType<typeof setTimeout>;
  invalidFeatureData: Array<Record<string, unknown>> = [];
  readerInfoSub: Subscription;
  statusObs$: Subscription;
  cnfBtnClicked = false;
  private sub: Subscription;
  modalRefUnSubscribe: NgbModalRef;
  pathVal: string;
  isShowCancelBtn: boolean = false;
  lineSwitchSupported: boolean;
  ampDimSupported: boolean;
  codedMainsSupported: boolean;
  dynadimmerSupported: boolean;
  interdependencyObjSetIdentified: boolean=false;
  boxConfigurationNoDeviceError  = "No device found!\nPlease check the device(s) and try again";
  configURL = "config/api/v1/configuration";

  OWP_Error = "Some features are OWP protected \n Please provide the correct OEM Write Protection password which currently locks the driver";
  formattedMemoryBanks: unknown;
  formattedResponse: unknown;
  owpError: boolean = false;
  service: FeatureUtilsService;
  // readerUrl: LegacyAny;
  // isTestCase: boolean=true;
  invalidFeatureDataflag: boolean= false;
  private readonly apiService: LegacyAny;

  constructor(

    public activemodal: NgbActiveModal,
    private readonly modalService: NgbModal,
    private readonly http: HttpClient,
    public route: Router,
    private readonly appState: AppStateService,
    private readonly uiSignals: UiSignalsStateService,
    private readonly deviceConfig: DeviceConfigStateService,
    private readonly utils: FeatureUtilsService,
    private readonly availFeat: AvailableFeaturesStateService,
    private readonly httpClient: MultiOneHttpClient,
    private readonly corruptFeatures: CorruptFeaturesService,
    private readonly owp: OwpStateService,
    private readonly dimmingInterfaceSvc: DimmingInterfaceStateService
  ) {
    const legacyApi = this.httpClient as LegacyAny;
    let cnfBtnClickedOverride: LegacyAny;
    let configDataOverride: LegacyAny;
    let closeModalOverride: LegacyAny;
    let readerInfoOverride: LegacyAny;

    legacyApi.modalClose = (value: string) => this.uiSignals.modalClose(value);
    legacyApi.readerInfo = (value: LegacyAny) => this.uiSignals.readerInfo(value);
    legacyApi.sendFeatures = (value: LegacyAny) => this.availFeat.sendFeatures(value);
    legacyApi.sendCorruptFeat = (value: LegacyAny) => this.corruptFeatures.sendCorruptFeat(value);
    legacyApi.sendNonCorrupt = (value: LegacyAny) => this.corruptFeatures.sendNonCorrupt(value);
    legacyApi.redFailedFeatures = (value: LegacyAny) => this.corruptFeatures.redFailedFeatures(value);
    legacyApi.setCnfBtnClicked = (value: boolean) => this.deviceConfig.setCnfBtnClicked(value);
    legacyApi.setBoxQuantityValue = (value: LegacyAny) => this.deviceConfig.setBoxQuantityValue(value);
    legacyApi.modalConfigurationData = (value: LegacyAny) => this.availFeat.modalConfigurationData(value);
    legacyApi.removeModes = (value: string) => this.uiSignals.removeModes(value);
    legacyApi.sendOwpPasswordSelected = (value: boolean) => this.owp.sendOwpPasswordSelected(value);
    legacyApi.sendOwpAccessPwd = (value: LegacyAny) => this.owp.sendOwpAccessPwd(value);

    Object.defineProperty(legacyApi, "showNextAndCancel", {
      get: () => this.deviceConfig.showNextAndCancel,
      set: (value: boolean) => {
        this.deviceConfig.showNextAndCancel = value;
      },
      configurable: true,
    });

    Object.defineProperty(legacyApi, "cnfBtnClicked$", {
      get: () => cnfBtnClickedOverride ?? this.deviceConfig.cnfBtnClicked$,
      set: (value: LegacyAny) => {
        cnfBtnClickedOverride = value;
      },
      configurable: true,
    });
    Object.defineProperty(legacyApi, "getconfigData", {
      get: () => configDataOverride ?? this.availFeat.getconfigData,
      set: (value: LegacyAny) => {
        configDataOverride = value;
      },
      configurable: true,
    });
    Object.defineProperty(legacyApi, "closeModal", {
      get: () => closeModalOverride ?? this.uiSignals.closeModal,
      set: (value: LegacyAny) => {
        closeModalOverride = value;
      },
      configurable: true,
    });
    Object.defineProperty(legacyApi, "getReaderInfo", {
      get: () => readerInfoOverride ?? this.uiSignals.getReaderInfo,
      set: (value: LegacyAny) => {
        readerInfoOverride = value;
      },
      configurable: true,
    });

    this.apiService = legacyApi;
    this.service = this.utils;
  }

  private getFeatureParameterPayload(feature: FeatureWithParameters | null | undefined): unknown {
    if (!feature || !feature.hasOwnProperty("featureParameters")) {
      return null;
    }

    const featureParameters = feature["featureParameters"];
    if (Array.isArray(featureParameters)) {
      const firstValidPayload = featureParameters.find(
        (parameter) => parameter !== null && parameter !== undefined
      );
      return firstValidPayload ?? null;
    }

    return featureParameters !== null && typeof featureParameters === "object"
      ? featureParameters
      : null;
  }

  ngOnInit() {
    const detectedReaderInfo = this.appState.getDetectedReaderInfo();
    if (detectedReaderInfo && detectedReaderInfo.selectedReader) {
      this.selectedReader = { ...detectedReaderInfo.selectedReader };
    }

    // Reset closeModal BehaviorSubject to prevent stale close events from previous modals
    this.apiService.modalClose("");

    this.sub = this.apiService.cnfBtnClicked$.subscribe(flag => {
      this.cnfBtnClicked = flag;
    });
    this.apiService.showNextAndCancel = false;
    this.isMultiDeviceChecked = this.appState.getStoreConcecutiveMultiple()
    this.pathVal = this.utils.getImageUrl(window.location.hostname);
    this.readerBaseUrl = environment.multiOneAPIBaseReadUrl;
    const multiDeviceConfig = this.appState.getMultidevice();
    const pluginout = this.appState.getPluginOut();
    if (pluginout != null && pluginout !== "") {
      this.mem = pluginout;
    }
    this.apiService.getconfigData.subscribe((data) => {
      this.readDevice = data;
    });
    this.apiService.closeModal.subscribe((data) => {

      // Block closure only if we're actively in the middle of async operations
      // Once operations complete (all flags false), allow closure even if read flow was active
      const isAsyncOperationInProgress = this.customerLoaderAfter || this.loading ||
        this.customerLoader || this.writingDevice;

      if (data && !isAsyncOperationInProgress) {
        this.activemodal.close();
      }
    });
    this.readerInfoSub = this.apiService.getReaderInfo.subscribe((data) => {
      if (data) {
        this.readerData = data;
        this.hideStatus = true;
        this.readerName = data;
        this.deviceInfoUrl = "/peripheral/v1/nfc/{peripheralId}/devices";
        // Start read flow immediately once reader info is available.
        this.dataInreader();
        /*  // setTimeout(() => {
         //   this.getListOfFeature();
         // }, 3000); */
      }
    });
    if (this.readDevice === "readDevice") {
      this.writingDevice = this.appState.getIsWriting();
      this.customerLoaderAfter = true;
      if (this.writingDevice) {
        this.readerName = this.appState.getReaderInfo();
        this.hideStatus = true;
        this.removeSuccessText = false;
        this.removeWritingHeader = true;
        this.writingDevicesContainer = true;
        this.isWritingRetryCheck = true;
        this.newReaderInfo();
      } else {
        this.isWritingRetryCheck = false;
        this.appState.setReadingDevice(true);
        this.customerLoaderAfter = true;
        const status = this.getDeviceReader();
        const readerSelection = this.getReaderList();

        this.statusObs$ = status
          .pipe(
            mergeMap((data) => {
              return readerSelection.pipe(
                catchError((err) => {
                  // do stuff with err from API1 request
                  return throwError(() => err);
                })
              );
            }),
            catchError((err) => {
              // do stuff with err from API2 request
              return throwError(() => err);
            })
          )
          .subscribe(
            (results) => {
              this.readerData = results;
              let detectedReaderInfo = this.appState.getDetectedReaderInfo();
              //Uncomment the below code to disable the box with LCN9640 reader on staging and production
              // const hostChecks = ["www.multione.signify.com", "staging"];
              // const isLCN9640OnDev =
              //   detectedReaderInfo.selectedReader == null ||
              //   (detectedReaderInfo.selectedReader &&
              //     detectedReaderInfo.selectedReader.peripheralType === "LCN9640") &&
              //   hostChecks.some(sub => window.location.hostname.includes(sub));
              //
              // if (isLCN9640OnDev) {
              //   this.errorMessage= "Reader not connected! Please check the reader and try again";
              //
              //   results=null;
              //   this.readerData = null;
              //   this.customerLoaderAfter = true;
              //
              //   localStorage.removeItem("readerDevices");
              //   localStorage.removeItem("readerInfo");
              //   let detectedReader = {
              //     readerType: "SimpleSet",
              //     selectedReader: null,
              //     readerConnected: false,
              //   };
              //
              //   localStorage.setItem(
              //     "detectedReaderInfo",
              //     JSON.stringify(detectedReader)
              //   );
              //   this.noReader = true;
              //
              //   this.showDCError(
              //     "Not found",
              //   this.errorMessage,
              //   true
              //   );
              //   this.retryButton = true;
              //   this.hideStatus = true;
              //   this.customerLoaderAfter = false;
              //   this.customerLoader = false;
              //
              // }

              this.appState.setReaderInfo(this.readerData);

              this.appState.setReaderDevices(results);

              const readers = this.appState.getDetectedReaderInfo();

              let newReader = false;
              // if (
              //   (isNullOrUndefined(readers && readers["selectedReader"]) ||
              //
              //     (readers["selectedReader"].peripheralType === "LCN9640" &&
              //     isLCN9640OnDev)
              // ) enable this and remove the below if to disable the box with LCN9640 reader on staging and production
              if (readers && readers["selectedReader"] == null
              ){
                newReader = true;
              } else if (
                (readers && readers["selectedReader"] != null &&
                  !this.readerData.some(r => r['peripheralType'] === readers["selectedReader"]['peripheralType']))
              ) {

                // if (
                //   (this.readerData.some(r => r['peripheralType'] === "LCN9640") ||
                //     readers["selectedReader"]?.['peripheralType'] === "LCN9640") &&
                //   hostChecks.some(sub => window.location.hostname.includes(sub))
                // ) {
                //   this.errorMessage = "Reader not connected! Please check the reader and try again";
                //   results = null;
                //   this.readerData = null;
                //   this.customerLoaderAfter = true;
                //   this.noReader = true;
                //   this.retryButton = true;
                //   this.hideStatus = true;
                //   this.customerLoaderAfter = false;
                //   this.customerLoader = false;
                //   localStorage.removeItem('readerInfo');
                // } uncomment this and remove the else block to disable the box with LCN9640 reader on staging and production

                // else{
                this.errorMessage = "Connected reader is different from originally selected reader! \n Please connect the originally selected reader and try again";
                results = null;
                this.readerData = null;
                this.customerLoaderAfter = true;
                this.noReader = false;
                this.readerPresence=true;

                this.showDCError(
                  "Not found",
                  this.errorMessage,
                  true
                );
                this.retryButton = true;
                this.hideStatus = true;
                this.customerLoaderAfter = false;
                this.customerLoader = false;
                this.appState.removeReaderInfo();
                newReader = true;

              }
              // } uncomment this and remove the above else block to disable the box with LCN9640 reader on staging and production
              else if (readers &&
                readers["selectedReader"] &&
                this.readerData.some(r => r['peripheralType'] === readers["selectedReader"]['peripheralType']))
              {




                const selectedReader = readers["selectedReader"];
                const connectedReader = this.readerData.find(r => r['peripheralId'] === selectedReader['peripheralId']) || this.readerData[0];

                if (connectedReader && selectedReader['peripheralId'] !== connectedReader['peripheralId']) {
                  this.errorMessage = "Connected reader is different from originally selected reader! \n Please connect the originally selected reader and try again";
                  results=null;
                  this.readerData = null;
                  this.customerLoaderAfter = true;


                  this.noReader = false;
                  this.readerPresence=true;

                  this.showDCError(
                    "Not found",
                    this.errorMessage,
                    true
                  );
                  this.retryButton = true;
                  this.hideStatus = true;
                  this.customerLoaderAfter = false;
                  this.customerLoader = false;

                  this.appState.removeReaderInfo();
                  newReader = true;

                }
              }

              if (
                !readers ||
                (readers && readers["selectedReader"] == null || newReader)
              ) {

                // Initialize detectedReaderInfo if it's null
                if (!detectedReaderInfo) {
                  detectedReaderInfo = {
                    selectedReader: null,
                    readerConnected: false
                  };
                }

                if (
                  readers &&
                  readers["selectedReader"] &&
                  this.readerData &&
                  Array.isArray(this.readerData) &&
                  this.readerData.some(
                    (reader) => reader.peripheralId === readers["selectedReader"]["peripheralId"]
                  )
                ) {
                  // If the selectedReader's peripheralId matches LegacyAny in readerData, assign that reader
                  const foundReader = this.readerData.find(
                    (reader) =>
                      reader.peripheralId === readers["selectedReader"]["peripheralId"]
                  );

                  if (foundReader) {
                    this.selectedReader = { ...foundReader }; // Assign the entire object
                  }
                }

                detectedReaderInfo.readerConnected = !!(
                  this.readerData &&
                  this.readerData[0]
                  // (
                  //   this.readerData[0].peripheralType !== "LCN9640" ||
                  //   (
                  //     this.readerData[0].peripheralType === "LCN9640" &&
                  //     !hostChecks.some(sub => window.location.hostname.includes(sub))
                  //   )
                  // ) uncomment this to disable the box with LCN9640 reader on staging and production
                );

                this.appState.setDetectedReaderInfo(detectedReaderInfo);
              }

              detectedReaderInfo.readerConnected = !!(
                this.readerData &&
                this.readerData[0]
                // (
                //   this.readerData[0].peripheralType !== "LCN9640" ||
                //   (
                //     this.readerData[0].peripheralType === "LCN9640" &&
                //     !hostChecks.some(sub => window.location.hostname.includes(sub))
                //   )
                // ) uncomment this to disable the box with LCN9640 reader on staging and production
              );
              this.appState.setDetectedReaderInfo(detectedReaderInfo);

              const checkDetectedreader = this.appState.getDetectedReaderInfo();

              if (results && checkDetectedreader.readerConnected) {
                this.hideStatus = true;
                if (this.readerData.length === 1) {
                  this.readerName = this.readerData[0];
                  this.dataInreader();
                } else {
                  if (this.isRetry || !this.isRetry ) {
                    const readerSelected = this.appState.getDetectedReaderInfo()?.selectedReader;

                    if (readerSelected && this.readerData) {
                      const matchingReader = this.readerData.find(
                        (reader) =>
                          reader.peripheralType === readerSelected.peripheralType &&
                          reader.peripheralId === readerSelected.peripheralId
                      );

                      if (matchingReader) {
                        this.readerName = matchingReader; // Assign the matching reader
                        this.dataInreader();
                      } else {

                        this.readerName = null; // Set to null if no match is found
                      }
                    } else {

                      this.readerName = null;
                    }
                  }



                }
              }

            },
            (error) => {
              this.customerLoaderAfter = true;
              this.appState.removeReaderDevices();
              this.appState.removeReaderInfo();
              const detectedReader = {
                readerType: "SimpleSet",
                selectedReader: this.selectedReader,
                readerConnected: false,
              };

              this.appState.setDetectedReaderInfo(detectedReader);
              this.noReader = true;
              if (error.status) {
                this.handleErrorCodedError(error);
              } else {
                if (error["Message"]) {
                  this.errorMessage = error["Message"];
                } else {
                  this.errorMessage = "Please check the connection";
                  this.retryButton = true;
                }
              }
              this.retryButton = true;
              this.hideStatus = true;
              this.customerLoaderAfter = false;
              this.customerLoader = false;
            }
          );

      }
    }


    this.url = environment.multiOneAPIUrl;
  }

  canshowNextAndCancel(){
    return this.apiService.showNextAndCancel;
  }

  showDCError(errorMessage: string, errorDescription: string, show: boolean) {
    if (show) {
      const modalRef = this.modalService.open(ErrorComponent, {
        keyboard: false,
        centered: true,
        windowClass: "error-style, error-style1",
        backdrop: 'static'
      });
      modalRef.componentInstance.errorName = errorMessage;
      modalRef.componentInstance.errorDescription = errorDescription;
    }
  }

  /**
   * Shared UI-reset used by the two "reader mismatch" branches inside
   * `newReaderInfo`. Callers set `this.errorMessage` first and then call
   * this. NOTE: this helper does NOT touch app-state cached reader info
   * (`removeReaderInfo()`); the caller is responsible for that when needed,
   * because the second (nested) mismatch branch intentionally leaves the
   * cached reader in place.
   */
  private applyReaderMismatchState(): void {
    this.readerData = null;
    this.customerLoaderAfter = true;
    this.noReader = false;
    this.readerPresence = true;
    this.showDCError("Not found", this.errorMessage, true);
    this.retryButton = true;
    this.hideStatus = true;
    this.customerLoaderAfter = false;
    this.customerLoader = false;
  }

  /**
   * Shared error-status → user-message mapping used by many of the API
   * subscribe(error) callbacks. Iterates `this.errorCodedDC` (populated by
   * the config service with `"<status>-<message>"` entries) and, when a
   * match is found, sets `this.errorMessage`, rewrites `err.message`
   * ("Reader not connected") for 404.1/412.1, and shows the error dialog.
   *
   * @param err     the HttpErrorResponse-like object (fields: `status`, `message`)
   * @param setRetryInside when true, sets `this.retryButton = true` inside the
   *                       matching branch (mirroring block-5/8 in newReaderInfo).
   */
  private handleErrorCodedError(err: LegacyAny, setRetryInside = false): void {
    this.errorCodedDC.forEach((errorCode) => {
      if (errorCode.includes(err.status)) {
        if (err.status === errorCode.split("-")[0]) {
          this.errorMessage = errorCode.split("-")[1];
          if (err.status === "404.1" || err.status === "412.1") {
            err.message = READER_NOT_CONNECTED_MSG;
          }
          this.showDCError(err.message, this.errorMessage, this.showExtraPopUp);
          if (setRetryInside) {
            this.retryButton = true;
          }
        }
      }
    });
  }

  newReaderInfo() {
    this.customerLoaderAfter = true;
      this.getReaderList().subscribe(
        (data) => {
          this.readerData = data;
          this.appState.setReaderInfo(this.readerData);

          this.appState.setReaderDevices(data);

          const readers = this.appState.getDetectedReaderInfo();

          let newReader = false;

          const detectedReaderInfo = this.appState.getDetectedReaderInfo();
          // const hostChecks = ["www.multione.signify.com", "staging"];
          // const isLCN9640OnDev =
          //   detectedReaderInfo.selectedReader == null ||
          //   (detectedReaderInfo.selectedReader &&
          //     detectedReaderInfo.selectedReader.peripheralType === "LCN9640") &&
          //   hostChecks.some(sub => window.location.hostname.includes(sub));
          //
          // if (isLCN9640OnDev) {
          //   this.errorMessage= "Reader not connected! Please check the reader and try again";
          //
          //   data=null;
          //   this.readerData = null;
          //   this.customerLoaderAfter = true;
          //
          //   localStorage.removeItem("readerDevices");
          //   localStorage.removeItem("readerInfo");
          //   let detectedReader = {
          //     readerType: "SimpleSet",
          //     selectedReader: null,
          //     readerConnected: false,
          //   };
          //
          //   localStorage.setItem(
          //     "detectedReaderInfo",
          //     JSON.stringify(detectedReader)
          //   );
          //   this.noReader = true;
          //
          //   this.showDCError(
          //     "Not found",
          //     this.errorMessage,
          //     true
          //   );
          //   this.retryButton = true;
          //   this.hideStatus = true;
          //   this.customerLoaderAfter = false;
          //   this.customerLoader = false;
          //
          // } uncomment this and remove the below if to disable the box with LCN9640 reader on staging and production
          // if (
          //   (isNullOrUndefined(readers && readers["selectedReader"]) ||
          //
          //     (readers["selectedReader"].peripheralType === "LCN9640" &&
          //       isLCN9640OnDev)
          //   )
          if (
            (readers && readers["selectedReader"] != null)
          ){
            newReader = true;
          }
          // NOTE: A previous `else if` branch here checked
          // `readers["selectedReader"] != null && !this.readerData.some(...)`
          // to show a "Connected reader is different" mismatch dialog based on
          // peripheralType. That branch was unreachable because the `if` above
          // matches whenever `selectedReader` is truthy (Sonar S2589 flagged
          // it as dead code). Removed to satisfy Sonar. The peripheralId
          // mismatch path below has the same unreachability issue but is left
          // in place pending product review of the intended reader-mismatch UX.
          else if (readers &&
            readers["selectedReader"] &&
            this.readerData.some(r => r['peripheralType'] === readers["selectedReader"]['peripheralType']))
          {

            const selectedReader = readers["selectedReader"];
            const connectedReader = this.readerData.find(r => r['peripheralId'] === selectedReader['peripheralId']) || this.readerData[0];

            if (connectedReader && selectedReader['peripheralId'] !== connectedReader['peripheralId']) {
              this.errorMessage = "Connected reader is different from originally selected reader! \n Please connect the originally selected reader and try again";
              data = null;
              this.applyReaderMismatchState();
              this.appState.removeReaderInfo();
              newReader = true;

            }
          }

          if (
            !readers ||
            (readers && readers["selectedReader"] == null || newReader)
          ) {


            if (
              readers &&
              readers["selectedReader"] &&
              this.readerData &&
              Array.isArray(this.readerData) &&
              this.readerData.some(
                (reader) => reader.peripheralId === readers["selectedReader"]["peripheralId"]
              )
            ) {
              // If the selectedReader's peripheralId matches LegacyAny in readerData, assign that reader
              const foundReader = this.readerData.find(
                (reader) =>
                  reader.peripheralId === readers["selectedReader"]["peripheralId"]
              );

              if (foundReader) {
                this.selectedReader = { ...foundReader }; // Assign the entire object
              }
            }

            detectedReaderInfo.readerConnected = !!(
              this.readerData &&
              this.readerData[0]
              // (
              //   this.readerData[0].peripheralType !== "LCN9640" ||
              //   (
              //     this.readerData[0].peripheralType === "LCN9640" &&
              //     !hostChecks.some(sub => window.location.hostname.includes(sub))
              //   )
              // ) uncomment this to disable the box with LCN9640 reader on staging and production
            );

            this.appState.setDetectedReaderInfo(detectedReaderInfo);
          }

          detectedReaderInfo.readerConnected = !!(
            this.readerData &&
            this.readerData[0]
            // (
            //   this.readerData[0].peripheralType !== "LCN9640" ||
            //   (
            //     this.readerData[0].peripheralType === "LCN9640" &&
            //     !hostChecks.some(sub => window.location.hostname.includes(sub))
            //   )
            // ) uncomment this to disable the box with LCN9640 reader on staging and production
          );
          this.appState.setDetectedReaderInfo(detectedReaderInfo);

          const checkDetectedreader=  this.appState.getDetectedReaderInfo();

          if (data && checkDetectedreader.readerConnected) {
            this.hideStatus = true;
            if (this.isRetry || !this.isRetry) {
              const readerSelected = this.appState.getDetectedReaderInfo()?.selectedReader;
              if (readerSelected && this.readerData) {
                const matchingReader = this.readerData.find(
                  (reader) =>
                    reader.peripheralType === readerSelected.peripheralType &&
                    reader.peripheralId === readerSelected.peripheralId
                );
                if (matchingReader) {
                  this.readerName = matchingReader; // Assign the matching reader
                  this.dataInreader();
                } else {
                  this.errorMessage = "Connected reader is different from originally selected reader! \n Please connect the originally selected reader and try again";
                  data = null;
                  this.applyReaderMismatchState();
                }
              } else {
                this.readerName = null;
              }
            }
          }


        },
        (error) => {
          this.removeWritingHeader = false;
          this.writingDevicesContainer = false;
          this.customerLoaderAfter = true;
          this.noReader = true;
          this.retryButton = true;
          this.appState.removeReaderDevices();
          const detectedReader = {
            readerType: "SimpleSet",
            selectedReader: this.selectedReader,
            readerConnected: false,
          };

          this.appState.setDetectedReaderInfo(detectedReader);
          if (error.status) {
            this.handleErrorCodedError(error);
          } else {
            if (error["Message"]) {
              this.errorMessage = error["Message"];
            } else {
              this.errorMessage = "Please check the connection";
              this.retryButton = true;
            }
          }
          this.hideStatus = true;
          this.customerLoaderAfter = false;
          this.customerLoader = false;
        }
      );
    //   }
    // });
  }
  getProductionInfo(url, deviceNfcId) {
    return this.httpClient.getProductionInfo(url, deviceNfcId);
  }
  dataInreader() {
    this.customerLoaderAfter = true;
    // this.service.getBaseUrl();
    // this.service.getReadData
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe((url) => {
    //     this.envHostName = url;
    //     if (url.length > 0) {
    //       this.readerBaseUrl = url;
    //       this.unsubscribe.next(undefined);
      // Optimization: Start getDeviceInfo immediately
      const deviceInfo$ = this.getDeviceInfo();

      // Check if we need to fetch plugin data in parallel or if we already have mem data
      const shouldFetchPlugin = !this.mem || this.mem.length === 0;

      let mainObservable$;
      if (shouldFetchPlugin) {
        // Run both in parallel only if we need plugin data
        const pluginOutput$ = this.fetchPluginOutputData();
        mainObservable$ = forkJoin([deviceInfo$, pluginOutput$]);
      } else {
        // If we already have mem data, only wait for device info
        mainObservable$ = deviceInfo$.pipe(
          tap(() => {
            // Plugin data will be null since we don't need it
          }),
          map(data => [data, null])
        );
      }

      mainObservable$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          ([data, pluginData]) => {
            // Store plugin data for later use (will be null if not fetched)
            const fetchedPluginData = pluginData;
            const length = Array.isArray(data) ? data.length : 0;
            this.appState.setDeviceDataLength(length);

            const arrayData = Array(data) as Array<Array<{ deviceNfcId: string }>>;
            this.deviceNfcIdsList = arrayData[0].map((item) => item.deviceNfcId);
            let parsedData: Array<{ deviceMetadata: { device12nc: string } }>;
            if (data instanceof ArrayBuffer) {
              const decoder = new TextDecoder("utf-8");
              const jsonString = decoder.decode(data);
              parsedData = JSON.parse(jsonString);
            } else {
              parsedData = data as Array<{ deviceMetadata: { device12nc: string } }>;
            }

            const reference12nc = parsedData[0].deviceMetadata.device12nc;
            const allSame = parsedData.every(
              item => item.deviceMetadata.device12nc === reference12nc
            );



            this.writingDevice = this.appState.getIsWriting();

            if(this.writingDevice){

              if (length > 1) {
                this.readerData = false;
                this.removeWritingHeader = false;
                this.writingDevicesContainer = false;
                this.noReader = false;
                this.readerPresence=true;
                this.customerLoader = false;
                this.loading = false;
                this.customerLoaderAfter = false;
                this.retryButton = true;
                this.writingDevicesContainer = false;
                this.errorMessage = "Multiple devices are found! \n Please check the device(s) and try again";
                this.showDCError("Error in finding devices", this.errorMessage, true);
                this.hideStatus = true;
                this.customerLoaderAfter = false;
                this.customerLoader = false;
                return; // Stop further execution
              }

            }

            if (!allSame) {
              data=null;

              this.readerData = false;
              this.removeWritingHeader = false;
              this.writingDevicesContainer = false;
              this.noReader = false;
              this.readerPresence=true;
              this.customerLoader = false;
              this.loading = false;
              this.customerLoaderAfter = false;
              this.retryButton = true;
              this.writingDevicesContainer = false;
              this.errorMessage =
                "All device types should be the same!\nPlease check the devices and try again";
              this.showDCError(
                "Multiple device types found",
                this.errorMessage,
                true
              );
              this.retryButton = true;
              this.hideStatus = true;
              this.customerLoaderAfter = false;
              this.customerLoader = false;
              return;

            }





            const deviceInfo = this.appState.getReaderDeviceFullInfo();
            const readingCheck = this.appState.getReadingDeviceString();
            if (
              deviceInfo && deviceInfo[0] && deviceInfo[0]["deviceName"]
              != null
            ) {
              data[0]["deviceMetadata"]["deviceName"] =
                deviceInfo[0]["deviceName"];
            }
            // if (data[0].deviceOwpStatus === "Enabled" && readingCheck === "false") {
            //   this.readerData = false;
            //   this.removeWritingHeader = false;
            //   this.writingDevicesContainer = false;
            //   this.noReader = true;
            //   this.customerLoader = false;
            //   this.loading = false;
            //   this.customerLoaderAfter = false;
            //   this.retryButton = true;
            //   this.writingDevicesContainer = false;
            //   this.errorMessage =
            //     "This device is OWP protected and therefore not accessible. Please select another device!";
            // }

            if (data) {
              this.hideStatus = true;
              setTimeout(() => {
                this.removeSuccessText = true;
              }, 100);
              this.readerDeviceData = data[0];
              this.appState.setSetReaderDEviceInfo(this.readerDeviceData);
              const configDeviceInfo = this.appState.getConfigurationData();
              this.isNormalFlow = this.appState.getNormalFlow();
              let checkDeviceSoftware;
              if (this.appState.getCompareFirmwareNoLatest()) {
                checkDeviceSoftware = this.appState.getCompareFirmwareNoLatest();
                if (checkDeviceSoftware.length > 3) {
                  checkDeviceSoftware = checkDeviceSoftware.slice(0, -2);
                }
              }
              if (
                configDeviceInfo &&
                configDeviceInfo["deviceName"] ===
                this.readerDeviceData.deviceMetadata["deviceName"] &&
                configDeviceInfo["device12nc"] ===
                this.readerDeviceData.deviceMetadata["device12nc"] &&
                checkDeviceSoftware ===
                this.readerDeviceData.deviceMetadata["deviceSwVersion"]
              ) {
                this.hideFeatures=false;
                this.hideStatus = true;
                this.removeSuccessText = false;
                this.removeWritingHeader = true;
                this.writingDevicesContainer = true;
                // this.service.getEnvValue();
                // this.service.getEnvData
                //   .pipe(takeUntil(this.unsubscribe))
                //   .subscribe((url) => {
                //     if (url.length > 0) {
                //       this.url = url;
                if(fetchedPluginData && fetchedPluginData.__pluginError){
                  // Feature-payload API returned an error — show it to the user
                  this.readerData = false;
                  this.invalidFeatureDataflag = true;
                  this.writingDevicesContainer = false;
                  this.noReader = false;
                  this.readerPresence = true;
                  this.customerLoader = false;
                  this.loading = false;
                  this.customerLoaderAfter = false;
                  const pluginErr = fetchedPluginData.error || {};
                  this.errorMessage = pluginErr["message"] || "Invalid Data.";
                  this.hideStatus = true;
                  const modalRef = this.modalService.open(ErrorComponent, {
                    keyboard: false,
                    centered: true,
                    windowClass: "error-style, error-style1",
                    backdrop: 'static'
                  });
                  modalRef.componentInstance.errorName = pluginErr["message"] || "Error";
                  modalRef.componentInstance.errorDescription = pluginErr["description"] || "Feature payload generation failed.";
                  this.isShowCancelBtn = true;
                }
                else if(this.mem.length !== 0 && this.mem!=null){
                  this.writingDeviceWithPC(this.mem).subscribe({
                    next: (writeResult) => {
                      //PWM device calculation
                      let parsedData: PwmWriteResult = writeResult as PwmWriteResult;
                      // If data is ArrayBuffer, try to parse it as JSON
                      if (writeResult instanceof ArrayBuffer) {
                        try {
                          const decoder = new TextDecoder("utf-8");
                          const jsonString = decoder.decode(new Uint8Array(writeResult));
                          parsedData = JSON.parse(jsonString);
                        } catch (e) {
                          parsedData = {};
                        }
                      }
                      if(parsedData.pwmDevice && parsedData.pwmDevice.readMemoryBanks) {
                        const memorybankIds: MemoryBankReference[] = [];
                        for (const memorybank of parsedData.pwmDevice.readMemoryBanks) {
                          memorybankIds.push({ memoryBankId: memorybank.memoryBankId });
                        }

                        this.getFormattedMemoryBanks(memorybankIds).subscribe((requiredCurrent) => {
                          // Assume requiredCurrent is the object you posted
                          const formattedProperties = requiredCurrent.formattedProperties;

                          // Find the AOC memory bank and get 'current'
                          const aocBank = formattedProperties.find(
                            prop => prop.memoryBankId === "114-0" && prop.memoryBankName === "AOC"
                          );
                          const currentValue = Number(aocBank?.parameters?.current ?? 0);

                          // Find the ALO memory bank and get 'percentage'
                          const aloBank = formattedProperties.find(
                            prop => prop.memoryBankId === "73-0" && prop.memoryBankName === "ALO"
                          );
                          const percentageValue = aloBank?.parameters?.enabled === true
                            ? Number(aloBank.parameters?.percentage ?? 100)
                            : 100;

                          // Calculate the required current
                          const calculatedCurrent = (currentValue * percentageValue) / 100;
                          const conversionTable = parsedData.pwmDevice.aocCurrent2PwmConversionTable || [];
                          const pwmValue = this.calculatePWM(conversionTable, calculatedCurrent);

                          // Write the PWM value back to the device
                          const pwmProperty = parsedData.pwmDevice.writeMemoryBanks?.[0];
                          if (!pwmProperty) {
                            return;
                          }
                          if (!pwmProperty.parameters) {
                            pwmProperty.parameters = {};
                          }
                          pwmProperty.parameters.enabled = aocBank?.parameters?.enabled === true;
                          pwmProperty.parameters.pulseWidth = Math.floor(pwmValue ?? 0);

                          requiredCurrent.iv = this.mem.iv;
                          requiredCurrent.metadata = this.mem.metadata;
                          requiredCurrent.owp = this.mem.owp;
                          requiredCurrent.formattedProperties.push(pwmProperty);
                          requiredCurrent.formattedProperties = requiredCurrent.formattedProperties.filter(
                            prop => prop !== aloBank
                          );
                          this.writePwmToDevice(requiredCurrent).subscribe({
                            next: (writePwmResult) => {
                              this.writingDevicesContainer = true;
                              this.readerData = true;
                              this.noReader = false;
                              this.customerLoader = false;
                              this.loading = false;
                              this.customerLoaderAfter = false;
                            },
                            error: (error) => {
                              this.writingDevicesContainer = false;
                              this.readerData = false;
                              this.noReader = true;
                              this.customerLoader = false;
                              this.loading = false;
                              this.customerLoaderAfter = false;
                            }
                          });
                        });
                      }
                    },
                    error: (error) => {}
                  });
                }
                else{
                  // Use the pre-fetched plugin data instead of making another API call
                    this.processPluginOutput(fetchedPluginData);
                  }
                //   }
                // });
              } else if (this.isNormalFlow) {
                this.readerData = false;
                this.noReader = false;
                this.removeWritingHeader = false;
                this.writingDevicesContainer = false;
                this.customerLoader = false;
                this.loading = false;
                this.customerLoaderAfter = false;
                this.readerPresence=true;
                this.showData = true;
                this.errorMessage =
                  "Type of devices found does not match type of device in configuration\nPlease retry with another device type";
                this.retryButton = true;
                this.showDCError(
                  "Incorrect device type found",
                  this.errorMessage,
                  this.showExtraPopUp
                );
              } else {
                this.deviceNfcId = data[0].deviceNfcId;
                // this.service.getEnvValue();
                // this.service.getEnvData
                //   .pipe(takeUntil(this.unsubscribe))
                //   .subscribe((url) => {
                //     if (url.length > 0) {
                this.showDeviceIDReadDC = true;
                this.getProductionInfo(this.url, this.deviceNfcId).subscribe(
                  (productionInfo: Record<string, LegacyAny>) => {
                    if (
                      productionInfo.hasOwnProperty(
                        "deviceProductionInformation"
                      )
                    ) {
                      this.isProductionInfoAvailable = true;
                      this.productionDate =
                        productionInfo.deviceProductionInformation.productionDate;
                      this.productionBatchId =
                        productionInfo.deviceProductionInformation.productionBatchId;
                      this.sku =
                        productionInfo.deviceProductionInformation.sku;

                      this.appState.setProdInfoStatus("yes");
                      //this.service.setshowContnueBtn(true);
                    } else {
                      this.isProductionInfoAvailable = false;
                      this.appState.setProdInfoStatus("no");
                      // this.service.setshowContnueBtn(false);
                    }
                  }
                );
                //   }
                // });
                //this.unsubscribe.next(undefined);
                this.showDeviceIDReadDC = false;

                this.searchWith12Nc(
                  this.readerDeviceData.deviceMetadata.device12nc
                );
                this.showContniueNowTimer = setInterval(() => {
                  const contniueSHowStatus = this.appState.getShowContinueNow();
                  if (contniueSHowStatus === "yes") {
                    this.continueRead = true;
                  } else {
                    this.continueRead = false;
                  }
                }, 1000);
              }
            }
          },
          (err) => {
            this.retryButton = true;
            this.readerData = false;
            this.removeWritingHeader = false;
            this.writingDevicesContainer = false;
            this.noReader = false;
            this.customerLoader = false;
            this.loading = false;
            this.customerLoaderAfter = false;
            const myObj = err;
            this.errorMessage = myObj.Message;
            if (err.status) {
              this.errorCodedDC.forEach((errorCode) => {
                if (errorCode.includes(err.status)) {
                  if (err.status === errorCode.split("-")[0]) {
                    if(this.readerName.peripheralType && this.readerName.peripheralType === 'LCN9640'){
                      this.errorMessage = this.boxConfigurationNoDeviceError;
                      err.message = "Error in finding devices";
                      this.readerPresence=true;
                    }
                    else {
                      this.errorMessage = errorCode.split("-")[1];
                      err.message = "Error in finding devices";
                      this.readerPresence=true;

                    }
                    if (err.status === "404.1" || err.status === "412.1") {
                      err.message = READER_NOT_CONNECTED_MSG;
                    }
                    this.showDCError(
                      err.message,
                      this.errorMessage,
                      this.showExtraPopUp
                    );
                  }
                }
              });
            }
            this.isShowCancelBtn = true;
          }
        );

  }

  searchWith12Nc(searchText) {
    this.specialErrorNoDatabase = false;
    const j = 0;
    this.regions = [...DEFAULT_DEVICE_REGIONS];
    const htHeader = new HttpHeaders()
      .set("region", this.regions[0])
      .append("region", this.regions[1])
      .append("region", this.regions[2])
      .append("region", this.regions[3]);
    this.searchTextValue(searchText, htHeader, j);
    this.searchName = [];
    forkJoin(this.apiList).subscribe(
      (resp) => {
        this.deviceInfo = [].concat.apply([], resp);
        if (this.deviceInfo.length > 0) {
          const boxquantityvalue = this.deviceInfo[0].boxQuantity ?? 0;
          this.apiService.setBoxQuantityValue(boxquantityvalue);
          this.appState.setReaderDeviceFullInfo(this.deviceInfo);
          try {
            this.appState.setNFCProtocol(this.deviceInfo[0].configurationInterface);
          } catch (error) { }

          // Chain the calls as requested
          this.getDeviceVersion().subscribe(() => {
            this.getListOfFeature();
          });


        } else {
          this.specialErrorNoDatabase = true;
          this.retryButton = true;
          this.isShowCancelBtn = true;
          this.readerData = false;
          this.noReader = true;
          this.customerLoader = false;
          this.loading = false;
          this.customerLoaderAfter = false;
          const deviceInfoUnAvailable =
            this.readerDeviceData.deviceMetadata;
          this.errorMessage =
            "This device is currently not supported \n \n" +
            deviceInfoUnAvailable.deviceName +
            " " +
            deviceInfoUnAvailable.deviceSwVersion +
            "\n 12nc: " +
            deviceInfoUnAvailable.device12nc +
            "\n \n Please contact your local Signify representative!";
          this.showDCError("Not supported", 'Device not supported! Please change the device and try again.', this.showExtraPopUp)
        }
      },
      (err) => {
        this.readerData = false;
        this.noReader = true;
        this.customerLoader = false;
        this.loading = false;
        this.customerLoaderAfter = false;
        const myObj = err;
        this.errorMessage = myObj.Message ? myObj.Message : myObj.message;
        if (this.errorMessage === undefined) {
          const modalRef = this.modalService.open(ErrorComponent, {
            keyboard: false,
            centered: true,
            windowClass: "error-style, error-style1",
            backdrop: 'static'
          });
          modalRef.componentInstance.errorName = "Error occured";
          modalRef.componentInstance.errorDescription = "Something went wrong! Please try again";
          this.errorMessage="Something went wrong! Please try again";
        }
        this.isShowCancelBtn = true;
      }
    );
  }


  searchTextValue(searchText, headers?, j?) {
    if (this.url && this.url.length) {
      this.envDeviceListURL =
        this.url + this.fullDeviceList + "?device12nc=";
      this.unsubscribe.next(undefined);
      this.apiList[j] = this.httpClient.apiCall(
        "get",
        this.envDeviceListURL + searchText + "&showArchive=true",
        "",
        { headers: headers }
      );
    }
  }

  getMemoreyBankList() {

    this.appState.setSetReaderDEviceInfo(this.readerDeviceData);
    const corruptedNfcSub: BehaviorSubject<string> = new BehaviorSubject(this.deviceNfcIdsList[0]);
    const compatibleFeat = this.checkCompatibleFeat().pipe(shareReplay(1));
    const corruptedNfcSubObs$ = corruptedNfcSub.asObservable();
    let count = 0;
    // this.service.getBaseUrl();
    // this.service.getReadData
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe((url) => {
    //     if (url.length > 0) {
    //       this.unsubscribe.next(undefined);
    corruptedNfcSubObs$.subscribe((nfcId) => {

      const corruptBit = this.checkCorruptBitByNfcId(nfcId);

      forkJoin([compatibleFeat, corruptBit]).subscribe(
        (results) => {
          count++;
          this.corruptBits = results[1];
          //this.featureWithId = results[0];
          this.featureWithId = JSON.parse(
            atob(results[0]["memoryBank"])
          );

          this.appState.setCompatibleFeatureID(this.featureWithId);
          let isCorrupted = false;
          const writeDeviceFeatures = this.appState.getWriteDeviceFeatures() || [];

          if (Array.isArray(this.corruptBits?.memoryBanks)) {
            this.corruptBits["memoryBanks"].forEach((element) => {
              let found = false;
              for (const feature of this.featureWithId) {
                if (element["memoryBankId"] === feature["memoryBankId"]) {
                  this.corruptFeatures.pushCorruptFeature(feature["featureName"], element["memoryBankId"]);
                  this.corrupt.push(feature["featureName"]);
                  found = true;
                  isCorrupted = true;

                }
              }
              if (!found) {
                this.corruptFeatures.pushCorruptFeature("unsupported", element["memoryBankId"]);
                this.corrupt.push("unsupported");
                isCorrupted = true;
              }
            })

          }
          if (isCorrupted) {
            this.corruptedDevicesCount++;
            this.appState.setCorruptedDevicesCount(this.corruptedDevicesCount);
          }
          if (count === this.deviceNfcIdsList.length) {

            corruptedNfcSub.unsubscribe();
            // this.isShowCancelBtn = true;
            if (this.corruptedDevicesCount > 0) {
              this.corruptedDevices = true;
              this.iscorrupt=true;
            } else {
              this.corruptedDevices = false;

            }
            this.customerLoaderAfter = true;
            this.loading = false;
            this.customerLoader = false;
            if ((
                this.mem == null || this.mem.length === 0) &&
              (
                this.selectedReader?.peripheralType !== "LCN9640" ||
                (this.selectedReader?.peripheralType === "LCN9640" && this.deviceNfcIdsList.length === 1)
              )
            ) {
              const isWriting = this.appState.getIsWriting();
              if (!isWriting) {
                this.getConfiguration();
              } else {
                this.getImportPayLoad();
              }
            } else if ((this.mem == null || this.mem.length === 0) && this.selectedReader?.peripheralType === "LCN9640") {
              this.buildConfigurationDataObj(this.deviceInfo[0]);
            }
          } else {
            corruptedNfcSub.next(this.deviceNfcIdsList[count])

          }

        },
        (err) => {
          this.isShowCancelBtn = true;
          this.feature12NcName = [];
          this.availFeat.sendFeatures(this.feature12NcName);
          this.readerData = false;
          this.noReader = true;
          this.customerLoader = false;
          this.loading = false;
          this.customerLoaderAfter = false;
          const myObj = err;
          this.errorMessage = myObj.Message;
          this.corruptbitfailed = true;
          if (err.status) {
            this.errorCodedDC.forEach((errorCode) => {
              if (errorCode.includes(err.status)) {
                if (err.status === errorCode.split("-")[0]) {
                  this.errorMessage = errorCode.split("-")[1];
                  if (err.status === "404.1" || err.status === "412.1") {
                    err.message = READER_NOT_CONNECTED_MSG;
                    this.showData = true;
                  }
                  this.showDCError(
                    err.message,
                    this.errorMessage,
                    this.showExtraPopUp
                  );
                  this.retryButton = true;
                }
              }
            });
          }
          else {
            if (
              this.errorMessage ===
              `An error occurred while retrieving the corrupt Memory Banks! \r\n ${DEVICE_OR_PERIPHERAL_RETRY_HINT}`
            ) {
              this.errorMessage =
                `An error occured while retrieving the corrupt data. ${DEVICE_OR_PERIPHERAL_RETRY_HINT}`;
              this.retryButton = true;
            }
          }
        }
      );
    });


  }
  getImportPayLoadwrite(statusCode: number) {
    setResultFeatures(this.appState,statusCode, this.uiSignals.getSelectedFeaturesArray(), this.corrupt, this.deviceInfo);
    this.customerLoaderAfter = false;
  }

  proceed() {

    this.appState.setNormalFlow(false);
    this.route.navigate(['/featureLanding']);
    this.apiService.setCnfBtnClicked(false);
    // setTimeout(() => {
    // window.location.reload();
    // }, 100);
  }

  checkCorruptBitByNfcId(deviceNfcId: string) {
    //return this.httpClient.apiCall('get', this.readerBaseUrl+this.corruptBitUrl.replace('{peripheralId}',this.readerName['peripheralType']).replace('{deviceNfcId}', this.readerDeviceData['deviceNfcId']));
    return this.httpClient.apiCall(
      "get",
      this.readerBaseUrl +
      this.corruptBitUrl
        .replace("{peripheralId}", this.readerName["peripheralId"])
        .replace("{deviceNfcId}", deviceNfcId)
    );
    // return this.httpClient.apiCall('get','/assets/reader/corruptBit.json');
  }


  callCorruptBnk() {
    // this.customerLoaderAfter = true;
    // this.service.getBaseUrl();
    // this.service.getReadData
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe((url) => {
    //     if (url.length > 0) {
    //       this.unsubscribe.next(undefined);
    this.checkCorruptBit().subscribe(
      (data: Record<string, LegacyAny>) => {
        if (data) {
          this.hideStatus = true;
          if (data.memoryBanks.length > 0) {
            data.memoryBanks.forEach((element) => {
              for (let i = 0; i < this.featureWithId.length; i++) {
                if (
                  element["memoryBankId"] ===
                  this.featureWithId[i]["memoryBankId"]
                ) {
                  this.corrupt.push(this.featureWithId[i]["featureName"]);
                }
              }
            });
          }
          if (this.writingDevice) {
            this.getImportPayLoad();
          }
        }
      },
      (err) => {
        this.feature12NcName = [];
        this.availFeat.sendFeatures(this.feature12NcName);
        this.readerData = false;
        this.writingDevicesContainer = false;
        this.noReader = true;
        this.customerLoader = false;
        this.loading = false;
        this.customerLoaderAfter = false;
        const myObj = err;
        this.errorMessage = myObj.Message;
        if (err.status) {
          this.handleErrorCodedError(err, true);
        }
      }
    );
    //   }
    // });
  }
  continueReadClicked() {
    // localStorage.setItem("readContinue", "yes");
    // this.uiSignals.modalClose("close");
    this.appState.setNormalFlow(false);
    const navigation: LegacyAny = this.route.navigate(["/featureLanding"]);
    if (navigation && typeof navigation.finally === "function") {
      navigation.finally(() => {
        this.activemodal.close();
      });
      return;
    }
    this.activemodal.close();
  }

  nextDeviceClicked() {
    this.hideFeatures = true;
    this.customerLoaderAfter = true;
    this.invalidFeatureData = [];
    this.retryButton=false;
    this.readerData = null;
    this.deviceConfig.showNextAndCancel = false;
    this.readerName = this.appState.getReaderInfo();
    this.hideStatus = true;
    this.removeSuccessText = false;
    this.customerLoader=false;
    this.removeWritingHeader = true;
    this.writingDevice=true;
    this.writingDevicesContainer = true;
    this.isWritingRetryCheck = true;
    const lastDeviceConfigData = this.appState.getReaderConfigurationData();

    let softwareVersion = this.appState.getCompareFirmwareNoLatest();
    if (softwareVersion && softwareVersion.length > 3) {
      softwareVersion = softwareVersion.slice(0, -2);
    }

    let mockDevice = {};
    mockDevice = {
      device12nc: lastDeviceConfigData.device12nc,
      softwareVersion: softwareVersion,
    };

    const selectedFeateForDCConfig = lastDeviceConfigData.selectedFeaturesInfo;
    const newConfigIddata = [];
    const apiCalls = [];
    if (selectedFeateForDCConfig.length >= 1) {
      //selectedFeateForDCConfig.forEach(() => {
      apiCalls.push(this.httpClient.getStartConfiguration(mockDevice, this.url))
      //});
    }
    forkJoin(apiCalls).subscribe(list => {
      list.forEach(data => {
        newConfigIddata.push(data["configurationID"]);
      })
      lastDeviceConfigData["configurationIDUniqueDC"] = newConfigIddata;

      // Preserve existing resp object with feature values
      const existingConfigData = this.appState.getConfigurationData();
      if (existingConfigData && existingConfigData["resp"] && !lastDeviceConfigData["resp"]) {
        lastDeviceConfigData["resp"] = existingConfigData["resp"];
      } else if (!lastDeviceConfigData["resp"]) {
        // Initialize empty resp object to ensure it exists for feature configurations
        lastDeviceConfigData["resp"] = {};
      }

      this.appState.setConfigurationData(lastDeviceConfigData);
      // Create a deep copy for SaveSetData to avoid reference issues
      const saveDataCopy = JSON.parse(JSON.stringify(lastDeviceConfigData));
      this.appState.setSaveSetData(saveDataCopy);
      this.appState.setSaveSetData1(saveDataCopy);


      this.hideFeatures = false;
      this.newReaderInfo();
    }, error => {
      this.customerLoaderAfter = false
    })

  }

  multipleDeviceCancel() {

    this.apiService.modalClose("close");
    // this.apiService.modalClose("close");
    this.activemodal.close();
    this.apiService.readerInfo("");
    this.unsubscribe.unsubscribe();
    this.apiService.sendFeatures("");
    this.apiService.sendCorruptFeat("");
    this.apiService.sendNonCorrupt("");
    this.apiService.redFailedFeatures("");
    this.apiService.setCnfBtnClicked(false);
  }

  getListOfFeature() {

    this.envScehmaLIstApi = this.url;
    this.getReaderData(
      this.readerDeviceData.deviceMetadata.device12nc,
      this.latestDvValue
    ).subscribe(
      (data) => {
        if (data) {
          this.removeSuccessText = false;
          this.allFeatures = data["metadata"]["schemas"];

          const schemas = data["metadata"]["schemas"];
          const features: string[] = [];
          schemas.forEach(schema => {
            const parts = schema.split(':');
            if (parts.length > 1) {
              const featureWithCode = parts[1];
              const featureName = featureWithCode.split('-')[0];
              features.push(featureName);
            }
          });

          this.availFeat.listOfAvailableFeatures = features;

          const tags = data["metadata"].tags;
          if(tags && tags.length > 0){
            this.appState.setTags(tags[0]);
          }else{
            this.appState.setTags(null);
          }

          if (this.allFeatures.length === 0) {
            this.activemodal.close();
            this.route.navigate(["/featureNonSupported"]);
          }
          this.allFeatures.forEach((element, index) => {
            const splitted = element.split(":")[1];
            if (splitted) {
              const feature12NCvalue = splitted.substring(
                0,
                splitted.length -
                this.readerDeviceData.deviceMetadata.device12nc.length
              );
              this.feature12NcName.push(feature12NCvalue);
            }
            const val = this.allFeatures.toString();
            const versiontoString =
              val.match(/[^,]*alo[^,]*/g) ||
              val.match(/[^,]*clo[^,]*/g) ||
              val.match(/[^,]*aoc[^,]*/g) ||
              val.match(/[^,]*ztv[^,]*/g);
            if (versiontoString) {
              this.softwareVersionSchema = versiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*ztv[^,]*/g)) {
              const ztvversiontoString = val.match(/[^,]*ztv[^,]*/g);
              this.ztvSoftwareVersionSchema = ztvversiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*ztvoutput[^,]*/g)) {
              const ztvoutputversiontoString =
                val.match(/[^,]*ztvoutput[^,]*/g);
              this.ztvoutputSoftwareVersionSchema =
                ztvoutputversiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*lip[^,]*/g)) {
              const lipversiontoString = val.match(/[^,]*lip[^,]*/g);
              if (lipversiontoString.length > 1) {
                this.lipSoftwareVersionSchema = lipversiontoString[1]
                  .toString()
                  .split(":")[2];
              } else {
                this.lipSoftwareVersionSchema = lipversiontoString
                  .toString()
                  .split(":")[2];
              }
            }
            if (val.match(/[^,]*rso[^,]*/g)) {
              const rsoversiontoString = val.match(/[^,]*rso[^,]*/g);
              this.rsoSoftwareVersionSchema = rsoversiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*alo[^,]*/g)) {
              const aloversiontoString = val.match(/[^,]*alo[^,]*/g);
              this.aloSoftwareVersionSchema = aloversiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*aoc[^,]*/g)) {
              const aocversiontoString = val.match(/[^,]*aoc[^,]*/g);
              this.aocSoftwareVersionSchema = aocversiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*touchdim[^,]*/g)) {
              const touchdimversiontoString =
                val.match(/[^,]*touchdim[^,]*/g);
              this.touchdimSoftwareVersionSchema = touchdimversiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*diia253maintenance[^,]*/g)) {
              const diia253maintenanceversiontoString = val.match(
                /[^,]*diia253maintenance[^,]*/g
              );
              this.diia253maintenanceSoftwareVersionSchema =
                diia253maintenanceversiontoString
                  .toString()
                  .split(":")[2];
            }
            if (val.match(/[^,]*corridor[^,]*/g)) {
              const corridorversiontoString =
                val.match(/[^,]*corridor[^,]*/g);
              this.corridorSoftwareVersionSchema = corridorversiontoString
                .toString()
                .split(":")[2];
            }

            if (val.match(/[^,]*luminfoformat0[^,]*/g)) {
              const luminversiontoString =
                val.match(/[^,]*luminfoformat0[^,]*/g);
              this.lumin0SoftwareVersionSchema = luminversiontoString
                .toString()
                .split(":")[2];
            }

            if (val.match(/[^,]*luminfoformat3[^,]*/g)) {
              const luminversiontoString =
                val.match(/[^,]*luminfoformat3[^,]*/g);
              this.lumin3SoftwareVersionSchema = luminversiontoString
                .toString()
                .split(":")[2];
            }

            if (val.match(/[^,]*luminfoformat4[^,]*/g)) {
              const luminversiontoString =
                val.match(/[^,]*luminfoformat4[^,]*/g);
              this.lumin4SoftwareVersionSchema = luminversiontoString
                .toString()
                .split(":")[2];
            }


            if (val.match(/[^,]*luminfoformat5[^,]*/g)) {
              const luminversiontoString =
                val.match(/[^,]*luminfoformat5[^,]*/g);
              this.lumin5SoftwareVersionSchema = luminversiontoString
                .toString()
                .split(":")[2];
            }

            if (val.match(/[^,]*dtl[^,]*/g)) {
              const dtlVersiontoString = val.match(/[^,]*dtl[^,]*/g);
              this.dtlSoftwareVersionSchema = dtlVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*dalipsu[^,]*/g)) {
              const dalipsuVersiontoString =
                val.match(/[^,]*dalipsu[^,]*/g);
              this.dalipsuSoftwareVersionSchema = dalipsuVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*mtp[^,]*/g)) {
              const mtpversiontoString = val.match(/[^,]*mtp[^,]*/g);
              this.mtpSoftwareVersionSchema = mtpversiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*dimminginterface[^,]*/g)) {
              const dimminginterfaceversiontoString = val.match(
                /[^,]*dimminginterface[^,]*/g
              );
              this.dimminginterfaceSoftwareVersionSchema =
                dimminginterfaceversiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*flextune[^,]*/g)) {
              const flextuneversiontoString = val.match(
                /[^,]*flextune[^,]*/g
              );
              this.flextuneSoftwareVersionSchema =
                flextuneversiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*multiconfigmode[^,]*/g)) {
              const multiconfigversiontoString = val.match(
                /[^,]*multiconfigmode[^,]*/g
              );
              this.multiconfigSoftwareVersionSchema =
                multiconfigversiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*clo[^,]*/g)) {
              const cloVersiontoString = val.match(/[^,]*clo[^,]*/g);
              this.cloSoftwareVersionSchema = cloVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*ampdim[^,]*/g)) {
              const ampdimVersiontoString = val.match(/[^,]*ampdim[^,]*/g);
              this.ampdimSoftwareVersionSchema = ampdimVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*clolite[^,]*/g)) {
              const cloliteVersiontoString =
                val.match(/[^,]*clolite[^,]*/g);
              this.cloliteSoftwareVersionSchema = cloliteVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*ast[^,]*/g)) {
              const astVersiontoString = val.match(/[^,]*ast[^,]*/g);
              this.astSoftwareVersionSchema = astVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*dynadim[^,]*/g)) {
              const dynadimVersiontoString =
                val.match(/[^,]*dynadim[^,]*/g);
              this.dynadimSoftwareVersionSchema = dynadimVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*dcemergency[^,]*/g)) {
              const dcemergencyVersiontoString = val.match(
                /[^,]*dcemergency[^,]*/g
              );
              this.dcemergencySoftwareVersionSchema =
                dcemergencyVersiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*lineswitch[^,]*/g)) {
              const lineswitchVersiontoString =
                val.match(/[^,]*lineswitch[^,]*/g);
              this.lineswitchSoftwareVersionSchema =
                lineswitchVersiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*mindimlevel[^,]*/g)) {
              const mindimlevelVersiontoString = val.match(
                /[^,]*mindimlevel[^,]*/g
              );
              this.mindimlevelSoftwareVersionSchema =
                mindimlevelVersiontoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*owp[^,]*/g)) {
              const owpVersiontoString = val.match(/[^,]*owp[^,]*/g);
              this.owpSoftwareVersionSchema = owpVersiontoString
                .toString()
                .split(":")[2];
            }
            if (val.match(/[^,]*daliaddressing102-[^,]*/g)) {
              const daliaddressing102toString = val.match(
                /[^,]*daliaddressing102-[^,]*/g
              );
              this.daliAddressing102SoftwareVersionSchema =
                daliaddressing102toString.toString().split(":")[2];
            }
            if (val.match(/[^,]*daliconfiguration102and207-[^,]*/g)) {
              const daliConfiguration102and207toString = val.match(
                /[^,]*daliconfiguration102and207-[^,]*/g
              );
              this.daliConfiguration102and207SoftwareVersionSchema =
                daliConfiguration102and207toString.toString().split(":")[2];
            }

            if (val.match(/[^,]*aux-[^,]*/g)) {
              const auxtoString = val.match(
                /[^,]*aux-[^,]*/g
              );
              this.auxSoftwareVersionSchema =
                auxtoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*lsi-[^,]*/g)) {
              const lsitoString = val.match(
                /[^,]*lsi-[^,]*/g
              );
              this.lsiSoftwareVersionSchema =
                lsitoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*aocmultichannel-[^,]*/g)) {
              const aocmultichanneltoString = val.match(
                /[^,]*aocmultichannel-[^,]*/g
              );
              this.aocmultichannelSoftwareVersionSchema =
                aocmultichanneltoString.toString().split(":")[2];
            }
            if (val.match(/[^,]*otp-[^,]*/g)) {
              const otpToString = val.match(
                /[^,]*otp-[^,]*/g
              );
              this.otpSoftwareVersionSchema =
                otpToString.toString().split(":")[2];
            }
            if (val.match(/[^,]*dsc-[^,]*/g)) {
              const dscToString = val.match(
                /[^,]*dsc-[^,]*/g
              );
              this.dscSoftwareVersionSchema =
                dscToString.toString().split(":")[2];
            }
            if (val.match(/[^,]*lowmainslock-[^,]*/g)) {
              const lowmainslockToString = val.match(
                /[^,]*lowmainslock-[^,]*/g
              );
              this.lowmainslockSoftwareVersionSchema =
                lowmainslockToString.toString().split(":")[2];
            }
            if (val.match(/[^,]*dipswitch-[^,]*/g)) {
              const dipswitchToString = val.match(
                /[^,]*dipswitch-[^,]*/g
              );
              this.dipswitchSoftwareVersionSchema =
                dipswitchToString.toString().split(":")[2];
            }
            if (val.match(/[^,]*codedmains-[^,]*/g)) {
              const codedmainsversiontoString = val.match(
                /[^,]*codedmains-[^,]*/g
              );
              this.codedmainsSoftwareVersionSchema =
                codedmainsversiontoString.toString().split(":")[2];
            }
            this.everySchemaofDevice = {
              flextuneSoftVer: this.flextuneSoftwareVersionSchema,
              multiconfigSoftVer: this.multiconfigSoftwareVersionSchema,
              swschema: this.softwareVersionSchema,
              luminSchemaVer0: this.lumin0SoftwareVersionSchema,
              luminSchemaVer3: this.lumin3SoftwareVersionSchema,
              luminSchemaVer4: this.lumin4SoftwareVersionSchema,
              luminSchemaVer5: this.lumin5SoftwareVersionSchema,
              dtlSchemaVer: this.dtlSoftwareVersionSchema,
              dalipsuSchemaVer: this.dalipsuSoftwareVersionSchema,
              mtpSoftVer: this.mtpSoftwareVersionSchema,
              dimminginterfaceSoftVer:
              this.dimminginterfaceSoftwareVersionSchema,
              cloSoftVer: this.cloSoftwareVersionSchema,
              aloSoftVer: this.aloSoftwareVersionSchema,
              aocSoftVer: this.aocSoftwareVersionSchema,
              touchdimSoftVer: this.touchdimSoftwareVersionSchema,
              diia253maintenanceSoftVer:
              this.diia253maintenanceSoftwareVersionSchema,
              corridorSoftVer: this.corridorSoftwareVersionSchema,
              ztvSoftVer: this.ztvSoftwareVersionSchema,
              ztvoutputSoftVer: this.ztvoutputSoftwareVersionSchema,
              lipSoftVer: this.lipSoftwareVersionSchema,
              rsoSoftVer: this.rsoSoftwareVersionSchema,
              ampdimSoftVer: this.ampdimSoftwareVersionSchema,
              astSoftVer: this.astSoftwareVersionSchema,
              cloliteSoftVer: this.cloliteSoftwareVersionSchema,
              dynadimmerSoftVer: this.dynadimSoftwareVersionSchema,
              dcemergencySoftVer: this.dcemergencySoftwareVersionSchema,
              lineswitchSoftVer: this.lineswitchSoftwareVersionSchema,
              mindimlevelSoftVer: this.mindimlevelSoftwareVersionSchema,
              owpSoftVer: this.owpSoftwareVersionSchema,
              codedmainsSoftVer: this.codedmainsSoftwareVersionSchema,
              dali102AddressingSoftVer: this.daliAddressing102SoftwareVersionSchema,
              dali102and207ConfigSoftVer: this.daliConfiguration102and207SoftwareVersionSchema,
              auxSoftVer: this.auxSoftwareVersionSchema,
              lsiSoftVer: this.lsiSoftwareVersionSchema,
              aocmultichannelSoftVer: this.aocmultichannelSoftwareVersionSchema,
              otpSoftVer: this.otpSoftwareVersionSchema,
              dscSoftVer: this.dscSoftwareVersionSchema,
              lowmainslockSoftVer: this.lowmainslockSoftwareVersionSchema,
              dipswitchSoftVer: this.dipswitchSoftwareVersionSchema
            };
            this.appState.setSoftwareSchema(this.everySchemaofDevice);
          });
          const writingDevice = this.appState.getIsWriting();
          this.customerLoaderAfter = true;
          if (!writingDevice) {
            this.hideStatus = true;
            this.getMemoreyBankList();
          }
          this.availFeat.sendFeatures(this.feature12NcName);
          this.hideStatus = true;
          // this.customerLoaderAfter = false;
        }
      },
      (err) => {
        this.retryButton = true;
        this.readerData = false;
        this.removeWritingHeader = false;
        this.writingDevicesContainer = false;
        this.noReader = true;
        this.customerLoader = false;
        this.loading = false;
        this.customerLoaderAfter = false;
        const myObj = err;
        this.errorMessage = myObj.message;
        if (err.code) {
          this.errorCodedDC.forEach((errorCode) => {
            if (errorCode.includes(err.code + "_1")) {
              if ("" + err.code + "_1" === errorCode.split("-")[0]) {
                if (err.status === "404.1" || err.status === "412.1") {
                  err.message = READER_NOT_CONNECTED_MSG;
                }
                this.errorMessage = errorCode.split("-")[1];
                this.showDCError(
                  err.message,
                  this.errorMessage,
                  this.showExtraPopUp
                );
              }
            }
          });
        }
        this.isShowCancelBtn = true;
      }
    );
    //   }
    // });
  }

  cancel() {

    this.apiService.modalClose("");
    this.apiService.readerInfo("");
    this.unsubscribe.unsubscribe();
    this.apiService.sendFeatures("");
    this.apiService.sendCorruptFeat("");
    this.apiService.sendNonCorrupt("");
    this.apiService.redFailedFeatures("");
    this.apiService.setCnfBtnClicked(false);
  }

  getConfiguration() {
    this.startConfigurationURL = this.url;
    this.configurationAPI(this.readerDeviceData.deviceMetadata).subscribe(
      (data) => {
        if (data) {
          this.hideStatus = true;
          this.startConfiguration = data;

          // Preserve configurationIDUniqueDC from configurationData if it exists
          const existingConfigData = this.appState.getConfigurationData();
          if (existingConfigData && existingConfigData["configurationIDUniqueDC"]) {
            this.startConfiguration["configurationIDUniqueDC"] = existingConfigData["configurationIDUniqueDC"];
          }

          this.appState.setReaderConfigurationData(this.startConfiguration);
          this.getFeaturePayload();
        }
      },
      (err) => {
        this.feature12NcName = [];
        this.availFeat.sendFeatures(this.feature12NcName);
        this.readerData = false;
        this.noReader = true;
        this.customerLoader = false;
        this.loading = false;
        this.customerLoaderAfter = false;
        const myObj = err;
        this.errorMessage = myObj.message;
        this.specialErrorNoDatabase = true;
        if (this.errorMessage === "Device type Not Exist.") {
          const deviceInfoUnAvailable =
            this.readerDeviceData.deviceMetadata;
          // this.errorMessage = 'Device is not available in our database. So we cannot read or write, please choose another device';
          this.errorMessage =
            "This device is currently not supported \n \n" +
            deviceInfoUnAvailable.deviceName +
            " " +
            deviceInfoUnAvailable.deviceSwVersion +
            "\n 12nc: " +
            deviceInfoUnAvailable.device12nc +
            "\n \n Please contact your local Signify representative!";
          this.showDCError("Not supported", 'Device not supported! Please change the device and try again.', this.showExtraPopUp)
        }
        this.retryButton = true;
        this.isShowCancelBtn = true;
      }
    );
    //   }
    // });
  }

  getFeaturePayload() {

    const memoryBankIdSet = new Set<string>();
    const supportedMB: { memoryBankId: string }[] = [];

    this.feature12NcName.forEach((featureMBName) => {
      const addMemoryBank = (id: string) => {
        if (!memoryBankIdSet.has(id)) {
          supportedMB.push({ memoryBankId: id });
          memoryBankIdSet.add(id);
        }
      };

      switch (featureMBName) {
        case "luminfoformat4-":
        case "luminfoformat5-":
        case "luminfoformat3-":
        case "luminfoformat0-":
          addMemoryBank("52-0");
          addMemoryBank("1-0");
          break;
        case "clo-":
        case "clolite-":
          addMemoryBank("14-0");
          break;
        case "ast-":
          addMemoryBank("29-0");
          break;
        case "srpsu-":
        case "dalipsu-":
          if (featureMBName === "dalipsu-" && this.feature12NcName.includes('srpsu-')) {
            break;
          }
          addMemoryBank("35-0");
          break;
        case "alo-":
          addMemoryBank("37-0");
          addMemoryBank("73-0");
          break;
        case "aoc-":
          addMemoryBank("114-0");
          addMemoryBank("149-0");
          break;
        case "ztv-":
          addMemoryBank("19-0");
          break;
        case "dynadimmer-":
          addMemoryBank("13-0");
          break;
        case "lineswitch-":
          addMemoryBank("15-0");
          break;
        case "ampdim-":
          addMemoryBank("18-0");
          break;
        case "dcemergency-":
          addMemoryBank("75-0");
          break;
        case "dtl-":
          addMemoryBank("74-0");
          break;
        case "mtp-":
          addMemoryBank("113-0");
          break;
        case "mindimlevel-":
          addMemoryBank("30-0");
          break;
        case "codedmains-":
          addMemoryBank("36-0");
          break;
        case "touchdim-":
          addMemoryBank("27-0");
          break;
        case "ztvoutput-":
          addMemoryBank("158-0");
          break;
        case "rso-":
          addMemoryBank("156-0");
          break;
        case "lip-":
          addMemoryBank("162-0");
          break;
        case "corridor-":
          addMemoryBank("16-0");
          break;
        case "diia253maintenance-":
          addMemoryBank("207-0");
          break;
        case "owp-":
          addMemoryBank("43-0");
          addMemoryBank("44-0");
          addMemoryBank("193-6");
          break;
        case "flextune-":
          addMemoryBank("121-0");
          addMemoryBank("119-0");
          break;
        case "multiconfigmode-":
          addMemoryBank("161-0");
          break;
        case "daliaddressing102-":
          addMemoryBank("95-0");
          break;
        case "daliconfiguration102and207-":
          addMemoryBank("95-0");
          addMemoryBank("96-0");
          break;
        case "aux-":
          addMemoryBank("190-6");
          break;
        case "lsi-":
          addMemoryBank("46-0");
          break;
        case "aocmultichannel-":
          addMemoryBank("193-3");
          break;
        case "otp-":
          addMemoryBank("133-0");
          break;
        case "dsc-":
          addMemoryBank("132-0");
          break;
        case "lowmainslock-":
          addMemoryBank("164-0");
          break;
        case "dipswitch-":
          addMemoryBank("134-0");
          break;
        default:
          break;
      }
    });

    const allPayResp = {
      memorybankIds: supportedMB,
    };
    this.unsubscribe.next(undefined);
    //this.httpClient.apiCall('post',this.readerBaseUrl+this.payLoadUrl.replace('{peripheralId}',this.readerName['peripheralType']).replace('{deviceNfcId}', this.readerDeviceData['deviceNfcId']),allPayResp)
    this.httpClient
      .apiCall(
        "post",
        this.readerBaseUrl +
        this.payLoadUrl
          .replace("{peripheralId}", this.readerName["peripheralId"])
          .replace(
            "{deviceNfcId}",
            this.readerDeviceData["deviceNfcId"]
          ),
        allPayResp
      )
      .subscribe(
        (resp) => {
          this.mem = resp;
          this.getImportPayLoad();
        },
        (err) => {
          this.feature12NcName = [];
          this.availFeat.sendFeatures(this.feature12NcName);
          this.writingDevicesContainer = false;
          this.readerData = false;
          this.customerLoader = false;
          this.loading = false;
          this.noReader = false;
          this.readerPresence = true;
          this.customerLoaderAfter = false;
          const myObj = err;
          this.errorMessage = myObj.Message;
          if (err.status) {
            this.errorCodedDC.forEach((errorCode) => {
              if (errorCode.includes(err.status)) {
                if (err.status === errorCode.split("-")[0]) {
                  this.errorMessage = errorCode.split("-")[1];
                  if (err.status === "404.1" || err.status === "412.1") {
                    this.showData = true;
                    this.noReader = true;
                    this.readerPresence = false;
                    err.message = READER_NOT_CONNECTED_MSG;
                  }
                  if (err.status === "405") {
                    this.showData = true;
                    err.message = "Device(s) not found"
                  }
                  this.showDCError(
                    err.message,
                    this.errorMessage,
                    this.showExtraPopUp
                  );
                  this.retryButton = true;
                }
              }
            });
          } else {
            if (
              this.errorMessage ===
              `An error occurred while returning the memory bank properties! \r\n ${DEVICE_OR_PERIPHERAL_RETRY_HINT}`
            ) {
              this.errorMessage =
                `An error occurred while returning the properties!  ${DEVICE_OR_PERIPHERAL_RETRY_HINT}`;
              this.retryButton = true;
            }
          }
          this.isShowCancelBtn = true;
        }
      );
    //   }
    // });
  }

  getImportPayLoad() {
    const configurationDataValue = this.appState.getConfigurationData();
    const readerConfigurationDataValue = this.appState.getReaderConfigurationData();

    let uniqueConfigIDs = [UNIQUE_CONFIG_ID_PLACEHOLDER];

    // Check readerConfigurationData first (most recent), then fall back to configurationData
    if (readerConfigurationDataValue != null && readerConfigurationDataValue["configurationIDUniqueDC"] != null) {
      uniqueConfigIDs = readerConfigurationDataValue["configurationIDUniqueDC"];
    } else if (configurationDataValue != null && configurationDataValue["configurationIDUniqueDC"] != null) {
      uniqueConfigIDs = configurationDataValue["configurationIDUniqueDC"];
    }
    let enyDryFlag = false;
    if (this.mem.hasOwnProperty("iv")) {
      this.envScehmaLIstApi = this.url + this.importPayLoadUrl;
      enyDryFlag = true;
    } else {
      this.envScehmaLIstApi = this.url + this.importPayLoadUrlNE;
      enyDryFlag = false;
    }

    this.availableFeatures = this.availFeat.getListOfAvailableFeatures();
    const hasDaliAddressing102 = this.availableFeatures.includes('daliaddressing102');
    const hasDaliConfiguration102and207 = this.availableFeatures.includes('daliconfiguration102and207');


    this.unsubscribe.next();
    let i = 0;
    this.apiListResponse = [];
    let newObj = [];
    const aloMinObj = [];
    const flextuneObj = [];
    const owpFeatureObj = [];
    const aocMinObj = [];
    const lumInfoSixObj = [];
    const daliconfiguratiob102and207Obj = [];
    const dynadimmerObj = [];
    const codedmainsObj = [];
    const ampDimObj = [];
    const lineswitchObj = [];
    let atleastOneObj: boolean = false;
    this.interdependencyObjSetIdentified = false;
    this.mem?.properties?.forEach((bankId) => {
      if (bankId.memoryBankId === "37-0") {
        this.isALoMinObj = true;

        if (enyDryFlag) {
          aloMinObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          aloMinObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "96-0") {
        this.isDalicongiuration102and207Obj = true;

        if (enyDryFlag) {
          daliconfiguratiob102and207Obj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          daliconfiguratiob102and207Obj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }





      if (bankId.memoryBankId === "121-0") {
        if (enyDryFlag) {
          flextuneObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          flextuneObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }

      if (bankId.memoryBankId === "43-0") {
        this.isOwpObj = true;

        if (enyDryFlag) {
          owpFeatureObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          owpFeatureObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "149-0") {
        this.isAocMinObj = true;

        if (enyDryFlag) {
          aocMinObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          aocMinObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "52-0") {
        this.isLumMinSixObj = true;

        if (enyDryFlag) {
          lumInfoSixObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          lumInfoSixObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "15-0") {
        this.lineSwitchSupported = true;

        if (enyDryFlag) {
          lineswitchObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          lineswitchObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "13-0") {
        this.dynadimmerSupported = true;

        if (enyDryFlag) {
          dynadimmerObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          dynadimmerObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "18-0") {
        this.ampDimSupported = true;

        if (enyDryFlag) {
          ampDimObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          ampDimObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }
      if (bankId.memoryBankId === "36-0") {
        this.codedMainsSupported = true;

        if (enyDryFlag) {
          codedmainsObj.push({
            memoryBankId: bankId.memoryBankId,
            ciphertext: bankId.ciphertext,
          });
        } else {
          codedmainsObj.push({
            memoryBankId: bankId.memoryBankId,
            memoryBankVersion: bankId.memoryBankVersion,
            payload: bankId.payload,
          });
        }
      }

    });
    this.mem?.properties?.forEach((bankId) => {
      this.bankIdByIndex = bankId.memoryBankId;
      atleastOneObj = false;

      if (
        bankId.memoryBankId === "19-0" ||
        bankId.memoryBankId === "35-0" ||
        bankId.memoryBankId === "1-0" ||
        bankId.memoryBankId === "114-0" ||
        bankId.memoryBankId === "73-0" ||
        bankId.memoryBankId === "14-0" ||
        bankId.memoryBankId === "149-0" ||
        bankId.memoryBankId === "29-0" ||
        bankId.memoryBankId === "27-0" ||
        bankId.memoryBankId === '15-0' ||
        bankId.memoryBankId === '13-0' ||
        bankId.memoryBankId === '18-0' ||
        bankId.memoryBankId === "75-0" ||
        bankId.memoryBankId === "74-0" ||
        bankId.memoryBankId === "113-0" ||
        bankId.memoryBankId === "30-0" ||
        bankId.memoryBankId === "156-0" ||
        bankId.memoryBankId === "162-0" ||
        bankId.memoryBankId === "158-0" ||
        bankId.memoryBankId === "16-0" ||
        bankId.memoryBankId === '36-0' ||
        bankId.memoryBankId === "207-0" ||
        bankId.memoryBankId === "44-0" ||
        bankId.memoryBankId === "119-0" ||
        bankId.memoryBankId === "161-0" ||
        bankId.memoryBankId === "95-0"||
        bankId.memoryBankId === "190-6" ||
        bankId.memoryBankId === "46-0" ||
        bankId.memoryBankId === "193-3" ||
        bankId.memoryBankId === "133-0" ||
        bankId.memoryBankId === "132-0" ||
        bankId.memoryBankId === "164-0" ||
        bankId.memoryBankId === "134-0"

      ) {

        if (bankId.memoryBankId === "73-0" && this.isALoMinObj) {
          newObj = [];
          if (enyDryFlag) {
            newObj.push({
              memoryBankId: bankId.memoryBankId,
              ciphertext: bankId.ciphertext,
            });
          } else {
            newObj.push({
              memoryBankId: bankId.memoryBankId,
              memoryBankVersion: bankId.memoryBankVersion,
              payload: bankId.payload,
            });
          }
          atleastOneObj = true;
          newObj = newObj.concat(aloMinObj);
        }


        else if (bankId.memoryBankId === "95-0") {
          // Handle special case for dali addressing/configuration
          if (hasDaliAddressing102 && hasDaliConfiguration102and207) {
            // Both features present - send two separate payloads
            // First payload: just 95-0 for daliaddressing102
            newObj = [];
            if (enyDryFlag) {
              newObj.push({
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              });
            } else {
              newObj.push({
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              });
            }
            atleastOneObj = true;
            // This will create the first API call with just 95-0
            // The second payload (95-0 + 96-0) will be added separately below
          } else if (hasDaliAddressing102 && !hasDaliConfiguration102and207) {
            // Only daliaddressing102 - send just 95-0
            newObj = [];
            if (enyDryFlag) {
              newObj.push({
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              });
            } else {
              newObj.push({
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              });
            }
            atleastOneObj = true;
          } else if (!hasDaliAddressing102 && hasDaliConfiguration102and207 && this.isDalicongiuration102and207Obj) {
            // Only daliconfiguration102and207 - send 95-0 with 96-0
            newObj = [];
            if (enyDryFlag) {
              newObj.push({
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              });
            } else {
              newObj.push({
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              });
            }
            newObj = newObj.concat(daliconfiguratiob102and207Obj);
            atleastOneObj = true;
          }
        }



        else if (bankId.memoryBankId === "119-0") {
          newObj = [];
          if (enyDryFlag) {
            newObj.push({
              memoryBankId: bankId.memoryBankId,
              ciphertext: bankId.ciphertext,
            });
          } else {
            newObj.push({
              memoryBankId: bankId.memoryBankId,
              memoryBankVersion: bankId.memoryBankVersion,
              payload: bankId.payload,
            });
          }
          atleastOneObj = true;
          newObj = flextuneObj.concat(newObj);
        } else if (bankId.memoryBankId === "44-0" && this.isOwpObj) {
          newObj = [];
          // Always include the 44-0 bank itself first
          if (enyDryFlag) {
            newObj.push({
              memoryBankId: bankId.memoryBankId,
              ciphertext: bankId.ciphertext,
            });
          } else {
            newObj.push({
              memoryBankId: bankId.memoryBankId,
              memoryBankVersion: bankId.memoryBankVersion,
              payload: bankId.payload,
            });
          }
          // If memory bank 193-6 exists, append it as well
          const mb1936 = this.mem?.properties?.find(p => p.memoryBankId === "193-6");

          if (mb1936) {
            if (enyDryFlag) {
              newObj.push({
                memoryBankId: mb1936.memoryBankId,
                ciphertext: mb1936.ciphertext,
              });
            } else {
              newObj.push({
                memoryBankId: mb1936.memoryBankId,
                memoryBankVersion: mb1936.memoryBankVersion,
                payload: mb1936.payload,
              });
            }
          }
          newObj = owpFeatureObj.concat(newObj);
          atleastOneObj = true;
        }

        else if (bankId.memoryBankId === "114-0" && this.isAocMinObj) {
          newObj = [];
          if (enyDryFlag) {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              },
            ];
          } else {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              },
            ];
          }
          atleastOneObj = true;
          newObj = newObj.concat(aocMinObj);
        } else if (bankId.memoryBankId === "1-0" && this.isLumMinSixObj) {
          newObj = [];
          if (enyDryFlag) {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              },
            ];
          } else {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              },
            ];
          }
          atleastOneObj = true;
          newObj = newObj.concat(lumInfoSixObj);
        } else if (bankId.memoryBankId === "13-0" && !this.interdependencyObjSetIdentified) {
          newObj = [];


          newObj = newObj.concat(dynadimmerObj).concat(lineswitchObj).concat(ampDimObj).concat(codedmainsObj);
          this.interdependencyObjSetIdentified = true;
          atleastOneObj = true;
        } else if (bankId.memoryBankId === "15-0" && !this.interdependencyObjSetIdentified) {
          newObj = [];


          newObj = newObj.concat(dynadimmerObj).concat(lineswitchObj).concat(ampDimObj).concat(codedmainsObj);
          this.interdependencyObjSetIdentified = true;
          atleastOneObj = true;
        } else if (bankId.memoryBankId === "18-0" && !this.interdependencyObjSetIdentified) {
          newObj = [];


          newObj = newObj.concat(dynadimmerObj).concat(lineswitchObj).concat(ampDimObj).concat(codedmainsObj);
          this.interdependencyObjSetIdentified = true;
          atleastOneObj = true;
        } else if (bankId.memoryBankId === "36-0" && !this.interdependencyObjSetIdentified) {
          newObj = [];


          newObj = newObj.concat(dynadimmerObj).concat(lineswitchObj).concat(ampDimObj).concat(codedmainsObj);
          this.interdependencyObjSetIdentified = true;
          atleastOneObj = true;
        } else if (bankId.memoryBankId === "27-0") {
          //let payloadMod = this.matchPayload15(bankId.payload)
          newObj = [];
          if (enyDryFlag) {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              },
            ];
          } else {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              },
            ];
          }
          atleastOneObj = true;
        }

        else if (
          bankId.memoryBankId === "19-0" ||
          bankId.memoryBankId === "35-0" ||
          bankId.memoryBankId === "1-0" ||
          bankId.memoryBankId === "114-0" ||
          bankId.memoryBankId === "14-0" ||
          bankId.memoryBankId === "73-0" ||
          bankId.memoryBankId === "29-0" ||
          bankId.memoryBankId === "75-0" ||
          bankId.memoryBankId === "74-0" ||
          bankId.memoryBankId === "113-0" ||
          bankId.memoryBankId === "30-0" ||
          bankId.memoryBankId === "16-0" ||
          bankId.memoryBankId === "207-0" ||
          bankId.memoryBankId === "158-0" ||
          bankId.memoryBankId === "156-0" ||
          bankId.memoryBankId === "162-0" ||
          bankId.memoryBankId === "161-0"||
          bankId.memoryBankId === "190-6" ||
          bankId.memoryBankId === "46-0" ||
          bankId.memoryBankId === "193-3" ||
          bankId.memoryBankId === "133-0" ||
          bankId.memoryBankId === "132-0" ||
          bankId.memoryBankId === "164-0" ||
          bankId.memoryBankId === "134-0"
        ) {
          newObj = [];
          if (enyDryFlag) {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                ciphertext: bankId.ciphertext,
              },
            ];
          } else {
            newObj = [
              {
                memoryBankId: bankId.memoryBankId,
                memoryBankVersion: bankId.memoryBankVersion,
                payload: bankId.payload,
              },
            ];
          }
          atleastOneObj = true;
        }

        const newConfigurationIDUnq = uniqueConfigIDs[0];



        // if (!(newConfigurationIDUnq === UNIQUE_CONFIG_ID_PLACEHOLDER)) {
        //   uniqueConfigIDs.shift();
        // }
        this.unsubscribe.next();
        //let uniqueConfigID =  this.UniqueCofigIDImportPayload();

        ///////////
        /*     this.service.getEnvValue();
     this.service.getEnvData.pipe(takeUntil(this.unsubscribe)).subscribe(url => {
       if (url.length > 0) {
         this.unsubscribe.next();
         this.startConfigurationURL = url;
         this.configurationAPI(this.readerDeviceData.deviceMetadata).subscribe((data) => {
           if (data) {
            this.apiListResponse[i] = this.httpClient.apiCall('post', this.envScehmaLIstApi.replace('{configurationId}', data['configurationID']), { "deviceName": this.mem.deviceName, "device12nc": this.mem.device12nc, "deviceSwVersion": this.mem.deviceSwVersion, "interface": this.mem.interface, "properties": newObj });

            //this.apiListResponse[i] = this.httpClient.apiCall('post', this.envScehmaLIstApi.replace('{configurationId}', this.startConfiguration['configurationID']), { "deviceName": this.mem.deviceName, "device12nc": this.mem.device12nc, "deviceSwVersion": this.mem.deviceSwVersion, "interface": this.mem.interface, "properties": newObj });
             i++;
           }
         }, err => {

         })
       }


     }); */

        /////Below chnage for passing new config id

        if (newConfigurationIDUnq !== UNIQUE_CONFIG_ID_PLACEHOLDER) {
          if (newConfigurationIDUnq === undefined) {
            let newConfigId = "";
            // this.service.getEnvValue();
            // this.service.getEnvData
            //   .pipe(takeUntil(this.unsubscribe))
            //   .subscribe((url) => {
            //     if (url.length > 0) {
            //       this.unsubscribe.next();
            this.startConfigurationURL = this.url;
            this.configurationAPI(
              this.readerDeviceData.deviceMetadata
            ).subscribe(
              (data) => {
                if (data) {
                  newConfigId = data["configurationID"];
                  if (atleastOneObj) {
                    this.apiListResponse[i] = this.httpClient.apiCall(
                      "post",
                      this.envScehmaLIstApi.replace(
                        "{configurationId}",
                        newConfigId
                      ),
                      this.buildFeatureConfigurationRequestPayload(newObj)
                    );
                  } else {
                    i--;
                  }
                }
              },
              (err) => {
              }
            );
            //   }
            // });
          } else {
            if (atleastOneObj) {
              this.apiListResponse[i] = this.httpClient.apiCall(
                "post",
                this.envScehmaLIstApi.replace(
                  "{configurationId}",
                  newConfigurationIDUnq
                ),
                this.buildFeatureConfigurationRequestPayload(newObj)
              );
            } else {
              i--;
            }
          }
          i++;
        }
        //////// Changes are eblow code
        else {
          if (atleastOneObj) {
            this.apiListResponse[i] = this.httpClient.apiCall(
              "post",
              this.envScehmaLIstApi.replace(
                "{configurationId}",
                this.startConfiguration["configurationID"]
              ),
              this.buildFeatureConfigurationRequestPayload(newObj)
            );
          } else {
            i--;
          }
          i++;
        }

      }
    });

// After the main forEach loop, add second payload for daliconfiguration102and207 when both features exist
    if (hasDaliAddressing102 && hasDaliConfiguration102and207 && this.isDalicongiuration102and207Obj) {
      const bank95 = this.mem?.properties?.find(p => p.memoryBankId === "95-0");
      if (bank95) {
        let daliConfigObj = [];
        if (enyDryFlag) {
          daliConfigObj.push({
            memoryBankId: bank95.memoryBankId,
            ciphertext: bank95.ciphertext,
          });
        } else {
          daliConfigObj.push({
            memoryBankId: bank95.memoryBankId,
            memoryBankVersion: bank95.memoryBankVersion,
            payload: bank95.payload,
          });
        }
        daliConfigObj = daliConfigObj.concat(daliconfiguratiob102and207Obj);

        // Add the API call for daliconfiguration102and207
        const configId = uniqueConfigIDs[0] !== UNIQUE_CONFIG_ID_PLACEHOLDER ? uniqueConfigIDs[0] : this.startConfiguration["configurationID"];
        const payload = this.buildFeatureConfigurationRequestPayload(daliConfigObj);

        this.apiListResponse[i] = this.httpClient.apiCall("post", this.envScehmaLIstApi.replace("{configurationId}", configId), payload);
        i++;
      }
    }

    forkJoin(this.apiListResponse).subscribe(
      (data) => {
        if (data) {
          this.hideStatus = true;
          const greenFeat = [];
          const redFeat = [];
          const popupFailedFeat = [];

          // Build resp object with all successful feature values
          const respObj = {};
          data.forEach((element) => {
            // Extract successful features and their values
            if (element["successfulFeatures"] && element["successfulFeatures"].length > 0) {
              element["successfulFeatures"].forEach((feature) => {
                const featurePayload = this.getFeatureParameterPayload(feature);
                if (feature["featureName"] && featurePayload) {
                  respObj[feature["featureName"]] = featurePayload;
                }
              });
            }
            // Also extract failed features that have parameters (completedwitherror case)
            if (element["failedFeatures"] && element["failedFeatures"].length > 0 &&
                element["status"] &&
                element["status"].split(" ").join("").toLowerCase().trim() === "completedwitherror") {
              element["failedFeatures"].forEach((feature) => {
                const featurePayload = this.getFeatureParameterPayload(feature);
                if (feature["featureName"] && featurePayload) {
                  respObj[feature["featureName"]] = featurePayload;
                }
              });
            }
          });

          // Merge resp object into existing ConfigurationData and SaveSetData (don't overwrite)
          let currentConfigData = this.appState.getConfigurationData();
          if (!currentConfigData) {
            currentConfigData = {};
          }
          if (!currentConfigData["resp"]) {
            currentConfigData["resp"] = {};
          }
          // Merge the new values into existing resp to preserve LegacyAny manually configured values
          Object.assign(currentConfigData["resp"], respObj);
          this.appState.setConfigurationData(currentConfigData);
          // Create a deep copy for SaveSetData to avoid reference issues
          const saveDataCopy = JSON.parse(JSON.stringify(currentConfigData));
          this.appState.setSaveSetData(saveDataCopy);
          this.appState.setSaveSetData1(saveDataCopy);
          data.forEach((element) => {
            let UnauthorizedErrorFeature: boolean = false;
            if (element["failedFeatures"].length === 1) {
              if (
                element["failedFeatures"][0].message === "Unauthorized."
              ) {
                UnauthorizedErrorFeature = true;
              }
            }

            if (!UnauthorizedErrorFeature) {
              if (
                element["successfulFeatures"].length > 0 ||
                (element["failedFeatures"].length > 0 &&
                  element["status"]
                    .split(" ")
                    .join("")
                    .toLowerCase()
                    .trim() === "completedwitherror")
              ) {
                if (
                  element["failedFeatures"].length > 0 &&
                  element["status"]
                    .split(" ")
                    .join("")
                    .toLowerCase()
                    .trim() === "completedwitherror"
                ) {
                  element["failedFeatures"].forEach((failedFeature) => {
                    const featurePayload = this.getFeatureParameterPayload(failedFeature);
                    if (
                      failedFeature != null &&
                      featurePayload != null
                    ) {
                      const invalidData = {};
                      invalidData["featureName"] = failedFeature["featureName"];
                      invalidData["featureParameters"] = featurePayload;
                      this.invalidFeatureData.push(invalidData);
                    } else if (
                      failedFeature != null &&
                      failedFeature["featureName"] &&
                      failedFeature["featureName"].toString().toUpperCase() === "LUMINFOFORMAT" &&
                      Array.isArray(failedFeature["descriptionList"]) &&
                      failedFeature["descriptionList"].some((d: string) =>
                        typeof d === "string" && d.toLowerCase().includes("invalid content format id")
                      )
                    ) {
                      // LumInfoFormat failed with a descriptionList but no featureParameters.
                      // Push it into invalidFeatureData following the same pattern above so
                      // supported-features.proceed() merges it into resp["LumInfoFormat"];
                      // the lum-info component then reads descriptionList from there.
                      const invalidData = {};
                      invalidData["featureName"] = failedFeature["featureName"];
                      invalidData["featureParameters"] = { descriptionList: failedFeature["descriptionList"] };
                      this.invalidFeatureData.push(invalidData);
                    } else {
                      greenFeat.push([failedFeature["featureName"]]);
                    }
                    greenFeat.push([failedFeature["featureName"]]);
                  });
                }
                greenFeat.push(element["successfulFeatures"]);
              } else if (element["failedFeatures"].length > 0) {
                greenFeat.push(element["failedFeatures"]);
              }
            }
            // if(element['successfulFeatures'].length>0) {
            //   greenFeat.push(element['successfulFeatures']);
            // } else if(element['failedFeatures'].length>0) {
            //   redFeat.push(element['failedFeatures']);
            // }
            UnauthorizedErrorFeature = false;
          });
          // this.corruptFeatures.redFailedFeatures(0);
          this.appState.setResultFeatures({
            green: greenFeat,
            red: redFeat,
            orange: this.corrupt,
            popupFailedFeat: popupFailedFeat,
            deviceInfo: this.deviceInfo,
          });

          if(redFeat.length){
            this.iscorrupt=true;
            this.isShowCancelBtn=true;
          }

          const resultFeatures = this.appState.getResultFeatures() || {};
          if (Array.isArray(resultFeatures.orange) && resultFeatures.orange.length > 0) {
            // orange exists and has items
            this.isShowCancelBtn = true;
          }

          // this.corruptFeatures.sendNonCorrupt({green: greenFeat, red: redFeat});
          // this.corruptFeatures.sendCorruptFeat(this.corrupt);
        }
        this.customerLoaderAfter = false;
        this.isReadFlowActive = false;
      },
      (err) => {
        this.feature12NcName = [];
        this.availFeat.sendFeatures(this.feature12NcName);
        this.readerData = false;
        this.noReader = true;
        this.customerLoader = false;
        this.loading = false;
        this.customerLoaderAfter = false;
        this.isReadFlowActive = false;
        const myObj = err;
        this.errorMessage = myObj.message;
        if (
          this.errorMessage ===
          `An error occurred while returning the memory bank properties! \r\n ${DEVICE_OR_PERIPHERAL_RETRY_HINT}`
        ) {
          this.errorMessage =
            `An error occurred while returning the properties!  ${DEVICE_OR_PERIPHERAL_RETRY_HINT}`;
          this.retryButton = true;
        }
        this.isShowCancelBtn = true;
      }
    );

  }

  // Helper method to fetch plugin output data for parallel execution
  fetchPluginOutputData(): Observable<unknown> {
    const configuration = this.appState.getConfigurationData();
    if (!configuration || !configuration["configurationID"]) {
      return of(null);
    }

    const writngUrl = this.url + this.writingURLViaPlugin;
    return this.httpClient.apiCall(
      "get",
      writngUrl.replace("{configurationId}", configuration["configurationID"])
    ).pipe(
      catchError((err) => of({ __pluginError: true, error: err }))
    );
  }

  // Process pre-fetched plugin output data
  processPluginOutput(data: unknown) {
    if (data) {
      this.mem = data;
      this.writingDeviceWithPC(data)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (writeResult) => {
            //PWM device calculation
            let parsedData: PwmWriteResult = writeResult as PwmWriteResult;
            // If data is ArrayBuffer, try to parse it as JSON
            if (writeResult instanceof ArrayBuffer) {
              try {
                const decoder = new TextDecoder("utf-8");
                const jsonString = decoder.decode(new Uint8Array(writeResult));
                parsedData = JSON.parse(jsonString);
              } catch (e) {
                parsedData = {};
              }
            }
            if(parsedData.pwmDevice && parsedData.pwmDevice.readMemoryBanks) {
              const memorybankIds: MemoryBankReference[] = [];
              for (const memorybank of parsedData.pwmDevice.readMemoryBanks) {
                memorybankIds.push({ memoryBankId: memorybank.memoryBankId });
              }

              this.getFormattedMemoryBanks(memorybankIds)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((requiredCurrent) => {
                  // Assume requiredCurrent is the object you posted
                  const formattedProperties = requiredCurrent.formattedProperties;

                  // Find the AOC memory bank and get 'current'
                  const aocBank = formattedProperties.find(
                    prop => prop.memoryBankId === "114-0" && prop.memoryBankName === "AOC"
                  );
                  const currentValue = Number(aocBank?.parameters?.current ?? 0);

                  // Find the ALO memory bank and get 'percentage'
                  const aloBank = formattedProperties.find(
                    prop => prop.memoryBankId === "73-0" && prop.memoryBankName === "ALO"
                  );
                  const percentageValue = aloBank?.parameters?.enabled === true
                    ? Number(aloBank.parameters?.percentage ?? 100)
                    : 100;

                  // Calculate the required current
                  const calculatedCurrent = (currentValue * percentageValue) / 100;
                  const conversionTable = parsedData.pwmDevice.aocCurrent2PwmConversionTable || [];
                  const pwmValue = this.calculatePWM(conversionTable, calculatedCurrent);

                  // Write the PWM value back to the device
                  const pwmProperty = parsedData.pwmDevice.writeMemoryBanks?.[0];
                  if (!pwmProperty) {
                    return;
                  }
                  if (!pwmProperty.parameters) {
                    pwmProperty.parameters = {};
                  }
                  pwmProperty.parameters.enabled = aocBank?.parameters?.enabled === true;
                  pwmProperty.parameters.pulseWidth = Math.floor(pwmValue ?? 0);

                  requiredCurrent.iv = this.mem.iv;
                  requiredCurrent.metadata = this.mem.metadata;
                  requiredCurrent.owp = this.mem.owp;
                  requiredCurrent.formattedProperties.push(pwmProperty);
                  requiredCurrent.formattedProperties = requiredCurrent.formattedProperties.filter(
                    prop => prop !== aloBank
                  );
                  this.writePwmToDevice(requiredCurrent)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe({
                      next: (writePwmResult) => {
                        this.writingDevicesContainer = true;
                        this.readerData = true;
                        this.noReader = false;
                        this.customerLoader = false;
                        this.loading = false;
                        this.customerLoaderAfter = false;
                      },
                      error: (error) => {
                        this.writingDevicesContainer = false;
                        this.readerData = false;
                        this.noReader = true;
                        this.customerLoader = false;
                        this.loading = false;
                        this.customerLoaderAfter = false;
                      }
                    });
                });
            }
          },
          error: (error) => {}
        });
    }
  }

  generatePluginOutput() {
    const configuration = this.appState.getConfigurationData();
    let writngUrl;
    writngUrl = this.url + this.writingURLViaPlugin;

    this.httpClient
      .apiCall(
        "get",
        writngUrl.replace(
          "{configurationId}",
          configuration["configurationID"]
        )
      )
      .subscribe(
        (data) => {
          if (data) {
            this.mem = data;
            this.writingDeviceWithPC(data).subscribe({
              next: (writeResult) => {
                //PWM device calculation
                let parsedData: PwmWriteResult = writeResult as PwmWriteResult;
                // If data is ArrayBuffer, try to parse it as JSON
                if (writeResult instanceof ArrayBuffer) {
                  try {
                    const decoder = new TextDecoder("utf-8");
                    const jsonString = decoder.decode(new Uint8Array(writeResult));
                    parsedData = JSON.parse(jsonString);
                  } catch (e) {
                    parsedData = {};
                  }
                }
                if(parsedData.pwmDevice && parsedData.pwmDevice.readMemoryBanks) {
                  const memorybankIds: MemoryBankReference[] = [];
                  for (const memorybank of parsedData.pwmDevice.readMemoryBanks) {
                    memorybankIds.push({ memoryBankId: memorybank.memoryBankId });
                  }

                  this.getFormattedMemoryBanks(memorybankIds).subscribe((requiredCurrent) => {
                    // Assume requiredCurrent is the object you posted
                    const formattedProperties = requiredCurrent.formattedProperties;

                    // Find the AOC memory bank and get 'current'
                    const aocBank = formattedProperties.find(
                      prop => prop.memoryBankId === "114-0" && prop.memoryBankName === "AOC"
                    );
                    const currentValue = Number(aocBank?.parameters?.current ?? 0);

                    // Find the ALO memory bank and get 'percentage'
                    const aloBank = formattedProperties.find(
                      prop => prop.memoryBankId === "73-0" && prop.memoryBankName === "ALO"
                    );
                    const percentageValue = aloBank?.parameters?.enabled === true
                      ? Number(aloBank.parameters?.percentage ?? 100)
                      : 100;

                    // Calculate the required current
                    const calculatedCurrent = (currentValue * percentageValue) / 100;
                    const conversionTable = parsedData.pwmDevice.aocCurrent2PwmConversionTable || [];
                    const pwmValue = this.calculatePWM(conversionTable, calculatedCurrent);

                    // Write the PWM value back to the device
                    const pwmProperty = parsedData.pwmDevice.writeMemoryBanks?.[0];
                    if (!pwmProperty) {
                      return;
                    }
                    if (!pwmProperty.parameters) {
                      pwmProperty.parameters = {};
                    }
                    pwmProperty.parameters.enabled = aocBank?.parameters?.enabled === true;
                    pwmProperty.parameters.pulseWidth = Math.floor(pwmValue ?? 0);
                    requiredCurrent.iv = this.mem.iv;
                    requiredCurrent.metadata = this.mem.metadata;
                    requiredCurrent.owp = this.mem.owp;
                    requiredCurrent.formattedProperties.push(pwmProperty);
                    requiredCurrent.formattedProperties = requiredCurrent.formattedProperties.filter(
                      prop => prop !== aloBank
                    );
                    this.writePwmToDevice(requiredCurrent).subscribe({
                      next: (writePwmResult) => {
                        this.writingDevicesContainer = true;
                        this.readerData = true;
                        this.noReader = false;
                        this.customerLoader = false;
                        this.loading = false;
                        this.customerLoaderAfter = false;
                      },
                      error: (error) => {
                        this.writingDevicesContainer = false;
                        this.readerData = false;
                        this.noReader = true;
                        this.customerLoader = false;
                        this.loading = false;
                        this.customerLoaderAfter = false;
                      }
                    });
                  });
                }
              },
              error: (error) => {}
            });
          }
        },
        (err) => {
          this.readerData = false;
          this.invalidFeatureDataflag=true;
          this.writingDevicesContainer = false;
          this.noReader = true;
          this.customerLoader = false;
          this.loading = false;
          this.customerLoaderAfter = false;
          const myObj = err;
          this.errorMessage = myObj.message;
          if (err.status) {
            this.handleErrorCodedError(err, true);
          } else {
            const modalRef = this.modalService.open(ErrorComponent, {
              keyboard: false,
              centered: true,
              windowClass: "error-style, error-style1",
              backdrop: 'static'
            });
            modalRef.componentInstance.errorName = err["message"];
            modalRef.componentInstance.errorDescription =
              err["description"];
          }
          this.isShowCancelBtn = true;
        }
      );

  }


  writingDeviceWithPC(data: unknown): Observable<unknown> {
    const detectedreader = this.appState.getDetectedReaderInfo();
    const readerName = detectedreader.selectedReader;
    const readerDeviceData = this.appState.getSetReaderDEviceInfo();
    const startConfiguration = this.appState.getConfigurationData();
    const i = 0;
    this.apiListResponse = [];
    return new Observable<unknown>((observer) => {
      this.writingURL = this.readerBaseUrl + this.writingInDeviceURL;

      const writingObj = data;
      return this.httpClient
        .putApi(
          "put",
          this.writingURL
            .replace("{peripheralId}", readerName["peripheralId"])
            .replace("{deviceNfcId}", readerDeviceData.deviceNfcId),
          writingObj

        )
        .subscribe(
          (data) => {
            //localStorage.removeItem("saveSetData");
            this.removeWritingHeader = false;
            this.removeSuccessText = false;
            this.readerData = this.appState.getReaderInfo();
            this.readerName = this.readerData[0];
            this.readerDeviceData = this.appState.getSetReaderDEviceInfo();
            this.startConfiguration = this.appState.getConfigurationData();
            this.feature12NcName["allFeature"] =
              startConfiguration["selectedFeaturesInfo"];
            this.availFeat.sendFeatures(this.feature12NcName["allFeature"]);
            this.getImportPayLoadwrite(200);
            observer.next(data);
            observer.complete();
          },
          (err) => {
            //OWP password error handling
            if(this.readerDeviceData?.deviceOwpStatus === "Enabled"
              && err.status === "401"
            ) {
              this.retryButton = true;
              this.readerData = false;
              this.writingDevicesContainer = false;
              this.noReader = false;
              this.owpError = true;
              this.customerLoader = false;
              this.loading = false;
              this.customerLoaderAfter = false;
              this.showData = true;
              const errorHeading = "Incorrect OWP password provided";
              this.errorMessage = "Some features are OWP protected \n Please provide the correct OEM Write Protection password which currently locks the driver";
              if (err.status) {
                this.showDCError(
                  errorHeading,
                  this.errorMessage,
                  true
                );
              }
              observer.error(err);
            } else {
              this.retryButton = true;
              this.readerData = false;
              this.writingDevicesContainer = false;
              this.noReader = true;
              this.customerLoader = false;
              this.loading = false;
              this.customerLoaderAfter = false;
              const myObj = err;
              this.errorMessage = myObj.Message;
              if (err.status) {
                this.errorCodedDC.forEach((errorCode) => {
                  if (errorCode.includes(err.status)) {
                    if (err.status === errorCode.split("-")[0]) {
                      this.errorMessage = errorCode.split("-")[1]
                      if (err.status === "404.1" || err.status === "412.1") {
                        this.showData = true;
                        err.message = READER_NOT_CONNECTED_MSG;
                      }
                      if (err.status === "422.2") {
                        this.showData = true;
                        err.message = "Device(s) not found"
                      }
                      this.showDCError(
                        err.message,
                        this.errorMessage,
                        this.showExtraPopUp
                      );
                      this.retryButton = true;
                    }
                  }
                });
              }
              this.isShowCancelBtn = true;
              observer.error(err);
            }
          }
        );
    });
  }


  getDeviceReader() {
    return this.httpClient.getServerStatus(this.readerBaseUrl);
    // return this.httpClient.apiCall('get', '/assets/reader/status.json');
  }
  getDeviceReader1() {
    return this.apiService.getServerStatus(this.readerBaseUrl);

    // return this.httpClient.apiCall('get', '/assets/reader/status.json');
  }
  getReaderList() {
    return this.httpClient.apiCall("get", this.readerBaseUrl + this.readerListUrl);
    // return this.httpClient.apiCall('get', '/assets/reader/list.json');
  }

  getDeviceInfo() {
    //return this.httpClient.apiCall('get', this.readerBaseUrl+this.deviceInfoUrl.replace('{peripheralId}',this.readerName['peripheralType']))
    if(!this.deviceInfoUrl.includes("?refresh=true")) {
      this.deviceInfoUrl = this.deviceInfoUrl.concat("?refresh=true");
    }


    return this.httpClient.apiCall(
      "get",
      this.readerBaseUrl +
      this.deviceInfoUrl.replace(
        "{peripheralId}",
        this.readerName["peripheralId"]
      )
    );
    // return this.httpClient.apiCall('get', '/assets/reader/readerInfo.json')
  }

  getMemoryBank() {
    return this.httpClient.apiCall(
      "get",
      this.readerBaseUrl +
      this.memoryBankUrl
        .replace("{peripheralId}", this.readerName["peripheralId"])
        .replace("{deviceNfcId}", this.readerDeviceData["deviceNfcId"])
    );

  }

  getReaderData(device12nc, deviceSwVersion) {
    return this.httpClient.getSupportedSchemaList(
      this.envScehmaLIstApi,
      device12nc,
      deviceSwVersion
    );
  }

  checkCompatibleFeat() {
    return of(environment.commonMemoryBank);
  }

  checkCorruptBit() {
    //return this.httpClient.apiCall('get', this.readerBaseUrl+this.corruptBitUrl.replace('{peripheralId}',this.readerName['peripheralType']).replace('{deviceNfcId}', this.readerDeviceData['deviceNfcId']));
    return this.httpClient.apiCall(
      "get",
      this.readerBaseUrl +
      this.corruptBitUrl
        .replace("{peripheralId}", this.readerName["peripheralId"])
        .replace("{deviceNfcId}", this.readerDeviceData["deviceNfcId"])
    );
    // return this.httpClient.apiCall('get','/assets/reader/corruptBit.json');
  }

  configurationAPI(deviceInfo) {
    const mockDevice = {
      device12nc: deviceInfo.device12nc,
      deviceName: deviceInfo.deviceName,
      softwareVersion: deviceInfo.deviceSwVersion,
    };
    return this.httpClient.getStartConfiguration(
      mockDevice,
      this.startConfigurationURL
    );
  }

  featurePayLoad() {
    //return this.httpClient.apiCall('get', this.readerBaseUrl+this.payLoadUrl.replace('{peripheralId}',this.readerName['peripheralType']).replace('{deviceNfcId}', this.readerDeviceData['deviceNfcId']).replace('{memorybankId}', this.bankIdByIndex));
    return this.httpClient.apiCall(
      "get",
      this.readerBaseUrl +
      this.payLoadUrl
        .replace("{peripheralId}", this.readerName["peripheralId"])
        .replace("{deviceNfcId}", this.readerDeviceData["deviceNfcId"])
        .replace("{memorybankId}", this.bankIdByIndex)
    );

    // return this.httpClient.apiCall('get','/assets/reader/payload.json');
  }

  importPayLoadAPI(payLoad) {
    return this.httpClient.apiCall(
      "post",
      this.envScehmaLIstApi.replace(
        "{configurationId}",
        this.startConfiguration["configurationID"]
      ),
      {
        deviceName: this.specificFeaturePayLoad.deviceName,
        device12nc: this.specificFeaturePayLoad.device12nc,
        deviceSwVersion: this.specificFeaturePayLoad.deviceSwVersion,
        interface: this.specificFeaturePayLoad.interface,
        properties: [payLoad],
      }
    );
  }

  getDeviceVersion(): Observable<unknown> {
    this.envAloschemaDetails = this.url;
    this.configurationData = this.readerDeviceData.deviceMetadata.device12nc;
    return this.getDeviceSoftwareVersion().pipe(
      tap((deviceVersion) => {
        const latestVersion = [];
        for (let i = deviceVersion["items"].length - 1; i >= 0; i--) {
          for (
            let j = deviceVersion["items"][i].softwareVersions.length - 1;
            j >= 0;
            j--
          ) {
            latestVersion.push(deviceVersion["items"][i].softwareVersions[j]);
            this.latestDeviceVersion.push(
              deviceVersion["items"][i].deviceType.nameAndVersion.split(":")[2]
            );
          }
        }
        this.latestDvValue = this.latestDeviceVersion[0];
      })
    );
  }

  getDeviceSoftwareVersion() {
    return this.httpClient.getDeviceSoftwareVersion(
      this.envAloschemaDetails,
      this.configurationData
    );
  }

  retry() {

    //OWP password error handling
    if(this.owpError) {
      this.apiService.sendOwpPasswordSelected(true);
      const modalRef = this.modalService.open(OwpPasswordComponent, {
        keyboard: false,
        centered: true,
        windowClass: "owp-popup-style",
        size: "xs",
        backdrop: 'static'
      });

      modalRef.componentInstance.sendOwpPwd.subscribe((pwd) => {
        const values = Object.values(pwd);
        if (values.every(v => v !== null)) {
          this.apiService.sendOwpAccessPwd(values);
        }
        this.apiService.modalConfigurationData("readDevice");
        this.activemodal.dismiss('OWP password entered');


        const modalRef = this.modalService.open(ReadDeviceComponent, {
          windowClass: "select-feature-modal",
          backdropClass: "customBackdrop",
        });
        modalRef.componentInstance.isRetry = true;
      });
    }

    //Normal error handling
    else if (this.isWritingRetryCheck) {
      //  this.cancel();
      this.apiService.modalClose("");
      this.apiService.readerInfo("");
      this.unsubscribe.unsubscribe();
      this.apiService.sendFeatures("");
      this.apiService.sendCorruptFeat("");
      this.apiService.sendNonCorrupt("");
      this.apiService.redFailedFeatures("");

      this.retryClicked = true;

      this.apiService.modalConfigurationData("readDevice");

      const modalRef = this.modalService.open(ReadDeviceComponent, {
        windowClass: "select-feature-modal",
        backdropClass: "customBackdrop",
      });
      modalRef.componentInstance.isRetry = true;
    } else {
      this.cancel();
      this.apiService.modalClose("");
      this.apiService.readerInfo("");
      this.unsubscribe.unsubscribe();
      this.apiService.sendFeatures("");
      this.apiService.sendCorruptFeat("");
      this.apiService.sendNonCorrupt("");
      this.apiService.redFailedFeatures("");

      this.retryClicked = true;
      this.appState.clearReadFlow();
      this.appState.removeCCTBoolen();
      this.appState.removeCorruptMemoryBankId();
      this.apiService.removeModes("invalid");

      this.apiService.modalConfigurationData("readDevice");

      const modalRef = this.modalService.open(ReadDeviceComponent, {
        windowClass: "select-feature-modal",
        backdropClass: "customBackdrop",
      });
      modalRef.componentInstance.isRetry = true;
    }

    //}
  }

  getFormattedMemoryBanks(memorybankIds: MemoryBankReference[]): Observable<FormattedMemoryResponse> {
    const detectedreader = this.appState.getDetectedReaderInfo();
    const readerName = detectedreader.selectedReader;
    const readerDeviceData = this.appState.getSetReaderDEviceInfo();

    this.apiListResponse = [];
    // this.service.getBaseUrl();
    // Return an Observable that emits when the URL is ready
    return new Observable<FormattedMemoryResponse>((observer) => {
      // this.service.getReadData
      //   .pipe(takeUntil(this.unsubscribe))
      //   .subscribe((url) => {
      //     if (url.length > 0) {
      this.writingURL = this.readerBaseUrl + this.formattedMemoryBankUrl;
      // this.readerBaseUrl = url;
      // this.unsubscribe.next(undefined);
      const allPayResp = {
        memorybankIds: memorybankIds,
      };

      return this.httpClient
        .apiCall(
          "post",
          this.writingURL
            .replace("{peripheralId}", readerName["peripheralId"])
            .replace("{deviceNfcId}", readerDeviceData.deviceNfcId),
          allPayResp
        )
        .subscribe(
          (result) => {
            observer.next(result as FormattedMemoryResponse);
            observer.complete();
          },
          (error) => {
            this.handleFormattedApiError(error);
            observer.error(error);
          }
        );
    });
  }

  writePwmToDevice(pwmProperty: unknown): Observable<unknown> {
    const detectedreader = this.appState.getDetectedReaderInfo();
    const readerName = detectedreader.selectedReader;
    const readerDeviceData = this.appState.getSetReaderDEviceInfo();

    this.apiListResponse = [];

    return new Observable<unknown>((observer) => {

      this.writingURL = this.readerBaseUrl + this.formattedMemoryBankUrl;
      this.httpClient
        .putApi(
          "put",
          this.writingURL
            .replace("{peripheralId}", readerName["peripheralId"])
            .replace("{deviceNfcId}", readerDeviceData.deviceNfcId),
          pwmProperty
        )
        .subscribe(
          (result) => {
            observer.next(result);
            observer.complete();
          },
          (error) => {
            observer.error(error);
            this.handleFormattedApiError(error);
          }
        );
      //   }
      // });
    });
  }

  calculatePWM(conversionTable: Array<{ aocCurrent: number; pulseWidth: number }>, targetCurrent: number): number | null {
    // Sort table by aocCurrent just in case
    const sorted = conversionTable.slice().sort((a, b) => a.aocCurrent - b.aocCurrent);

    // If targetCurrent is outside the range, return the closest value
    if (targetCurrent <= sorted[0].aocCurrent) return sorted[0].pulseWidth;
    if (targetCurrent >= sorted[sorted.length - 1].aocCurrent) return sorted[sorted.length - 1].pulseWidth;

    // Find the two points for interpolation
    for (let i = 0; i < sorted.length - 1; i++) {
      const x0 = sorted[i].aocCurrent;
      const y0 = sorted[i].pulseWidth;
      const x1 = sorted[i + 1].aocCurrent;
      const y1 = sorted[i + 1].pulseWidth;

      targetCurrent = Math.floor(targetCurrent);
      if (targetCurrent >= x0 && targetCurrent <= x1) {
        // Linear interpolation formula
        return y0 + ((y1 - y0) * (targetCurrent - x0)) / (x1 - x0);
      }
    }
    return null; // Should not reach here
  }

  private handleFormattedApiError(error: ApiErrorLike): void {
    this.utils.formattedApiCallError = true;
    this.retryButton = true;
    this.readerData = false;
    this.hideFeatures = true;
    this.writingDevicesContainer = false;
    this.customerLoader = false;
    this.loading = false;
    this.customerLoaderAfter = false;
    const myObj = error;
    this.errorMessage = myObj.Message;
    if (error.status) {
      this.errorCodedDC.forEach((errorCode) => {
        if (errorCode.includes(String(error.status))) {
          if (error.status === errorCode.split("-")[0]) {
            this.errorMessage = errorCode.split("-")[1];
            if (error.status === "404.1" || error.status === "412.1") {
              this.showData = true;
              error.message = READER_NOT_CONNECTED_MSG;
            }
            if (error.status === "422.2") {
              this.showData = true;
              error.message = "Device(s) not found";
            }
            this.showDCError(
              error.message,
              this.errorMessage,
              this.showExtraPopUp
            );
            this.retryButton = true;
          }
        }
      });
    }
    this.isShowCancelBtn = true;
  }



  retryConfigure() {
    this.newReaderInfo();
  }
  ngOnDestroy() {
    this.apiService.modalClose("");
    this.apiService.readerInfo("");
    //this.unsubscribe.unsubscribe();
    this.apiService.sendFeatures("");
    this.utils.formattedApiCallError = false;
    this.apiService.sendCorruptFeat("");
    this.apiService.sendNonCorrupt("");
    this.apiService.redFailedFeatures("");
    if (this.readerInfoSub) {
      this.readerInfoSub.unsubscribe();
    }
    if (this.statusObs$) {
      this.statusObs$.unsubscribe();
    }
    if (this.modalRefUnSubscribe) {
      this.modalRefUnSubscribe.close();
    }
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.showContniueNowTimer) {
      clearInterval(this.showContniueNowTimer);
    }
  }

  buildConfigurationDataObj(deviceArg) {
    let latestFirmware = this.readerDeviceData.deviceMetadata.deviceSwVersion;
    this.appState.setCompareFirmwareNoLatest(latestFirmware);
    this.envScehmaLIstApi = this.url;
    this.getReaderData(
      this.readerDeviceData.deviceMetadata.device12nc,
      this.latestDvValue
    ).subscribe((resp) => {
      this.customerLoaderAfter = false;
      const unSupportedFeatCheck = resp["metadata"].schemas;
      const tags = resp["metadata"].tags;
      if(tags && tags.length > 0){
        this.appState.setTags(tags[0]);
      }else{
        this.appState.setTags(null);
      }
      const supportedSchema: string[] = [];
      for (
        let index = 0;
        index < unSupportedFeatCheck.length;
        index++
      ) {
        const element = unSupportedFeatCheck[index];
        supportedSchema.push(element);
      }
      resp["metadata"].schemas = supportedSchema;
      if (resp["metadata"].schemas.length) {
        this.allFeatures = resp["metadata"].schemas;

        this.feature12NcName = this.feature12NcName.filter(Boolean);
        const val = this.allFeatures.toString();
        const versiontoString =
          val.match(/[^,]*alo[^,]*/g) ||
          val.match(/[^,]*clo[^,]*/g) ||
          val.match(/[^,]*aoc[^,]*/g) ||
          val.match(/[^,]*ztv[^,]*/g) ||
          val.match(/[^,]*ast[^,]*/g) ||
          val.match(/[^,]*luminfoformat0[^,]*/g) ||
          val.match(/[^,]*luminfoformat3[^,]*/g) ||
          val.match(/[^,]*dalipsu[^,]*/g);

        if (versiontoString) {
          this.softwareVersionSchema = versiontoString
            .toString()
            .split(":")[2];
        }

        const selectedFeaturesInfo = [];
        if (val.match(/[^,]*multiconfigmode[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Multiconfig");
          const multiconfigversiontoString = val.match(
            /[^,]*multiconfigmode[^,]*/g
          );
          this.multiconfigSoftwareVersionSchema =
            multiconfigversiontoString.toString().split(":")[2];
        }

        if (val.match(/[^,]*alo[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Alo");
        }
        if (val.match(/[^,]*clo-[^,]*/g)) {
          if (
            val
              .match(/[^,]*clo-[^,]*/g)
              .toString()
              .split(":")[1]
              .slice(
                0,
                val
                  .match(/[^,]*clo-[^,]*/g)
                  .toString()
                  .split(":")[1]
                  .indexOf(deviceArg.device12nc)
              ) === "clo-"
          ) {
            this.appendSelectedFeature(selectedFeaturesInfo, "Clo");
            const cloversiontoString = val.match(/[^,]*clo-[^,]*/g);
            this.cloSoftwareVersionSchema = cloversiontoString
              .toString()
              .split(":")[2];
          }
        }
        if (val.match(/[^,]*aoc[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Aoc");
          const aocversiontoString = val.match(/[^,]*aoc[^,]*/g);
          this.aocSoftwareVersionSchema = aocversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*touchdim[^,]*/g)) {
          const touchdimversiontoString =
            val.match(/[^,]*touchdim[^,]*/g);
          this.touchdimSoftwareVersionSchema = touchdimversiontoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "touchdim");
        }
        if (val.match(/[^,]*diia253maintenance[^,]*/g)) {
          const diia253maintenanceversiontoString = val.match(
            /[^,]*diia253maintenance[^,]*/g
          );
          this.diia253maintenanceSoftwareVersionSchema =
            diia253maintenanceversiontoString
              .toString()
              .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "diia253maintenance");
        }
        if (val.match(/[^,]*ast[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_STARTUP_TIME);
          const astversiontoString = val.match(/[^,]*ast[^,]*/g);
          this.astSoftwareVersionSchema = astversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*dimminginterface[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Dimminginterface");
          const dimminginterfaceversiontoString = val.match(
            /[^,]*dimminginterface[^,]*/g
          );
          this.dimminginterfaceSoftwareVersionSchema =
            dimminginterfaceversiontoString.toString().split(":")[2];
        }
        if (val.match(/[^,]*mtp[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Mtp");
          const mtpversiontoString = val.match(/[^,]*mtp[^,]*/g);
          this.mtpSoftwareVersionSchema = mtpversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*srpsu[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "SR PSU");
        }

        if (val.match(/[^,]*luminfoformat0[^,]*/g)) {
          const luminversiontoString =
            val.match(/[^,]*luminfoformat0[^,]*/g);
          this.lumin0SoftwareVersionSchema = luminversiontoString
            .toString()
            .split(":")[2];
        }

        if (val.match(/[^,]*luminfoformat3[^,]*/g)) {
          const luminversiontoString =
            val.match(/[^,]*luminfoformat3[^,]*/g);
          this.lumin3SoftwareVersionSchema = luminversiontoString
            .toString()
            .split(":")[2];
        }

        if (val.match(/[^,]*luminfoformat4[^,]*/g)) {
          const luminversiontoString =
            val.match(/[^,]*luminfoformat4[^,]*/g);
          this.lumin4SoftwareVersionSchema = luminversiontoString
            .toString()
            .split(":")[2];
        }


        if (val.match(/[^,]*luminfoformat5[^,]*/g)) {
          const luminversiontoString =
            val.match(/[^,]*luminfoformat5[^,]*/g);
          this.lumin5SoftwareVersionSchema = luminversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*ztv[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_ZTV);
          const ztvversiontoString = val.match(/[^,]*ztv[^,]*/g);
          this.ztvSoftwareVersionSchema = ztvversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*ztvoutput[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "ztvoutput");
          const ztvoutputversiontoString =
            val.match(/[^,]*ztvoutput[^,]*/g);
          this.ztvoutputSoftwareVersionSchema =
            ztvoutputversiontoString.toString().split(":")[2];
        }
        if (val.match(/[^,]*lip[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "lip");
          const lipversiontoString = val.match(/[^,]*lip[^,]*/g);
          if (lipversiontoString.length > 1) {
            this.lipSoftwareVersionSchema = lipversiontoString[1]
              .toString()
              .split(":")[2];
          } else {
            this.lipSoftwareVersionSchema = lipversiontoString
              .toString()
              .split(":")[2];
          }
        }
        if (val.match(/[^,]*rso[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "rso");
          const rsoversiontoString = val.match(/[^,]*rso[^,]*/g);
          this.rsoSoftwareVersionSchema = rsoversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*clolite[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_CLO_LITE);
          const cloliteversiontoString =
            val.match(/[^,]*clolite[^,]*/g);
          this.cloliteSoftwareVersionSchema = cloliteversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*corridor-[^,]*/g)) {
          const corridorversiontoString =
            val.match(/[^,]*corridor[^,]*/g);
          this.corridorSoftwareVersionSchema = corridorversiontoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "Corridor");
        }
        if (val.match(/[^,]*dcemergency-[^,]*/g)) {
          const dcemergencyVersiontoString = val.match(
            /[^,]*dcemergency-[^,]*/g
          );
          this.dcemergencySoftwareVersionSchema =
            dcemergencyVersiontoString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "DC Emergency");
        }
        if (val.match(/[^,]*touchdim-[^,]*/g)) {
          const touchdimversiontoString =
            val.match(/[^,]*touchdim[^,]*/g);
          this.touchdimSoftwareVersionSchema = touchdimversiontoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_TOUCH_AND_DIM);
        }
        if (val.match(/[^,]*diia253maintenance-[^,]*/g)) {
          const diia253maintenanceversiontoString = val.match(
            /[^,]*diia253maintenance[^,]*/g
          );
          this.diia253maintenanceSoftwareVersionSchema =
            diia253maintenanceversiontoString
              .toString()
              .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "diia253maintenance");
        }
        if (val.match(/[^,]*dynadimmer-[^,]*/g)) {
          const dynadimmerVersiontoString = val.match(
            /[^,]*dynadimmer-[^,]*/g
          );
          this.dynadimSoftwareVersionSchema =
            dynadimmerVersiontoString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_DYNA_DIMMER);
        }
        if (val.match(/[^,]*lineswitch-[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_LINE_SWITCH);
          const lineswitchversiontoString = val.match(
            /[^,]*lineswitch-[^,]*/g
          );
          this.lineswitchSoftwareVersionSchema =
            lineswitchversiontoString.toString().split(":")[2];
        }
        if (val.match(/[^,]*owp-[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Owp");
          const owpversiontoString = val.match(/[^,]*owp-[^,]*/g);
          this.owpSoftwareVersionSchema = owpversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*codedmains-[^,]*/g)) {
          this.appendSelectedFeature(selectedFeaturesInfo, "Codedmains");
          const codedmainsversiontoString = val.match(
            /[^,]*codedmains-[^,]*/g
          );
          this.codedmainsSoftwareVersionSchema =
            codedmainsversiontoString.toString().split(":")[2];
        }
        if (val.match(/[^,]*dalipsu-[^,]*/g)) {
          const dalipsuversiontoString =
            val.match(/[^,]*dalipsu-[^,]*/g);
          this.dalipsuSoftwareVersionSchema = dalipsuversiontoString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*daliaddressing102-[^,]*/g)) {
          const daliaddressing102toString =
            val.match(/[^,]*daliaddressing102-[^,]*/g);
          this.daliAddressing102SoftwareVersionSchema = daliaddressing102toString
            .toString()
            .split(":")[2];
        }
        if (val.match(/[^,]*daliconfiguration102and207-[^,]*/g)) {
          const daliconfiguration102and207toString = val.match(
            /[^,]*daliconfiguration102and207-[^,]*/g
          );
          this.daliConfiguration102and207SoftwareVersionSchema = daliconfiguration102and207toString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "Dali Configuration 102 and 207");
        }
        if (val.match(/[^,]*aux-[^,]*/g)) {
          const auxtoString = val.match(
            /[^,]*aux-[^,]*/g
          );
          this.auxSoftwareVersionSchema = auxtoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_AUX_OUTPUT_VOLTAGE);
        }
        if (val.match(/[^,]*lsi-[^,]*/g)) {
          const lsitoString = val.match(
            /[^,]*lsi-[^,]*/g
          );
          this.lsiSoftwareVersionSchema = lsitoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "LSI");
        }

        if (val.match(/[^,]*aocmultichannel-[^,]*/g)) {
          const aocmultichanneltoString = val.match(
            /[^,]*aocmultichannel-[^,]*/g
          );
          this.aocmultichannelSoftwareVersionSchema = aocmultichanneltoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_AOC_MULTI_CHANNEL);
        }

        if (val.match(/[^,]*otp-[^,]*/g)) {
          const otpToString = val.match(
            /[^,]*otp-[^,]*/g
          );
          this.otpSoftwareVersionSchema =
            otpToString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "OTP");
        }

        if (val.match(/[^,]*dsc-[^,]*/g)) {
          const dscToString = val.match(
            /[^,]*dsc-[^,]*/g
          );
          this.dscSoftwareVersionSchema =
            dscToString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "DSC");
        }

        if (val.match(/[^,]*lowmainslock-[^,]*/g)) {
          const lowmainslockToString = val.match(
            /[^,]*lowmainslock-[^,]*/g
          );
          this.lowmainslockSoftwareVersionSchema =
            lowmainslockToString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_LOW_MAINS_LOCK);
        }

        if (val.match(/[^,]*dipswitch-[^,]*/g)) {
          const dipswitchToString = val.match(
            /[^,]*dipswitch-[^,]*/g
          );
          this.dipswitchSoftwareVersionSchema =
            dipswitchToString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_DIPSWITCH);
        }


        if (val.match(/[^,]*ampdim-[^,]*/g)) {
          const ampdimVersiontoString = val.match(/[^,]*ampdim[^,]*/g);
          this.ampdimSoftwareVersionSchema = ampdimVersiontoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_AMP_DIM);
        }
        if (val.match(/[^,]*dtl[^,]*/g)) {
          const dtlVersiontoString = val.match(/[^,]*dtl[^,]*/g);
          this.dtlSoftwareVersionSchema = dtlVersiontoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "DTL");
        }
        if (val.match(/[^,]*flextune[^,]*/g)) {
          const flextuneVersiontoString =
            val.match(/[^,]*flextune[^,]*/g);
          this.flextuneSoftwareVersionSchema = flextuneVersiontoString
            .toString()
            .split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, "Flextune");
        }
        if (val.match(/[^,]*mindimlevel[^,]*/g)) {
          const mindimlevelVersiontoString = val.match(
            /[^,]*mindimlevel[^,]*/g
          );
          this.mindimlevelSoftwareVersionSchema =
            mindimlevelVersiontoString.toString().split(":")[2];
          this.appendSelectedFeature(selectedFeaturesInfo, FEATURE_LABEL_MIN_DIM_LEVEL);
        }

        const device = this.deviceInfo[0];
        if (device) {
          const domain = "https://www.api.easydesignintool.signify.com";
          device.devicetypePicture = device.devicetypePicture
            ? device.devicetypePicture
            : device.picture;
          if (device.devicetypePicture !== undefined) {
            device.devicetypePicture =
              domain + device.devicetypePicture.replace(domain, "");
          }
        }
        this.url = this.envHostName;
        let mockDevice = {};
        if (latestFirmware.length > 3) {
          latestFirmware = latestFirmware.slice(
            0,
            -2
          );
        }
        if (deviceArg.deviceName === "XH075C120V075KPF1") {
          mockDevice = {
            device12nc: deviceArg.device12nc,
            softwareVersion: latestFirmware,
          };
        } else {
          mockDevice = {
            device12nc: deviceArg.device12nc,
            deviceName: deviceArg.deviceName,
            softwareVersion: latestFirmware,
          };
        }
        this.httpClient
          .getStartConfiguration(
            mockDevice,
            this.envScehmaLIstApi
          )
          .subscribe(
            (data) => {

              // localStorage.setItem("readerConfigurationData", JSON.stringify(data));

              this.appState.removeDeviceVersion();
              let configurationID;
              if (this.appState.getConfigId()) {
                configurationID = this.appState.getConfigId();
              } else {
                configurationID = data["configurationID"];
              }

              this.appState.setUniqueConfigIdSet(configurationID);

              const feature12Nc = [
                ...new Set(this.feature12NcName),
              ];
              const cleanedFeatures = this.feature12NcName.map(name => name.replace(/-$/, ''));
              this.availFeat.sendFeatures(cleanedFeatures);
              // let greenFeat: string[] = cleanedFeatures.filter(
              //   feat => !this.corrupt.some(corruptItem => corruptItem.toLowerCase() === feat.toLowerCase())


              // );

              this.hideStatus = true;
              let greenFeat: unknown[] = [];
              const redFeat: unknown[] = [];
              const popupFailedFeat: unknown[] = [];

              // Capitalize and wrap each feature name in an array
              greenFeat = cleanedFeatures.map(feat => [formatFeatureName(feat)]);

// Helper function to format feature names (e.g., capitalize)
              function formatFeatureName(name: string): string {
                // Example: convert to PascalCase
                return name
                  .replace(/(^\w|_\w)/g, m => m.replace('_', '').toUpperCase());
              }





              const availableFeaturesSet = new Set(
                (this.availFeat.listOfAvailableFeatures || [])
                  .map(name => normalizeFeatureName(name))
                  .filter(Boolean)
              );


// If this.corrupt is an array of strings:
              const corruptedFeaturesSet = new Set(
                (this.corrupt || [])
                  .map(name => normalizeFeatureName(name))
                  .filter(Boolean)
              );



              const filteredCorruptedFeatures = Array.from(corruptedFeaturesSet)
              //.filter(name => availableFeaturesSet.has(name));


              function normalizeFeatureName(rawName: string): string | null {
                if (!rawName || typeof rawName !== "string") return null;
                rawName = rawName.trim().toLowerCase();
                for (const { name, pattern, exclude } of FEATURE_PATTERNS) {
                  if (exclude && exclude.test(rawName)) continue;
                  if (pattern.test(rawName)) {
                    const normalized = FEATURE_NORMALIZATION[name.toLowerCase()];
                    return normalized || name;
                  }
                }
                return FEATURE_NORMALIZATION[rawName] || null;
              }

              this.appState.setResultFeatures({
                green: greenFeat,
                red: redFeat,
                orange: filteredCorruptedFeatures,
                popupFailedFeat:popupFailedFeat,
                deviceInfo: this.deviceInfo,
              });



              if(filteredCorruptedFeatures.length > 0){
                this.iscorrupt=true;
              }else{
                this.iscorrupt=false;
              }


              const resultFeatures = this.appState.getResultFeatures() || {};
              if (Array.isArray(resultFeatures.orange) && resultFeatures.orange.length > 0) {
                // orange exists and has items
                this.isShowCancelBtn = true;
              }

              // Initialize configurationData as an object
              this.configurationData = {};
              this.configurationData["configurationID"] =
                configurationID;
              this.configurationData["deviceName"] =
                deviceArg["deviceName"];
              this.configurationData["softwareVersion"] =
                deviceArg["softwareVersion"];
              this.configurationData["picture"] =
                deviceArg["devicetypePicture"];
              this.configurationData[
                "selectedFeaturesInfo"
                ] = [];
              this.configurationData["softwareVerSchema"] =
                this.softwareVersionSchema;
              this.assignConfigurationVersionFields();
              this.configurationData["device12nc"] =
                deviceArg.device12nc;
              this.configurationData["feature12Nc"] =
                feature12Nc;

              // Preserve existing resp object with feature values (from read or configure operations)
              const existingConfigData = this.appState.getConfigurationData();
              if (existingConfigData && existingConfigData["resp"]) {
                this.configurationData["resp"] = existingConfigData["resp"];
              } else {
                // Initialize empty resp object to ensure it exists for feature configurations
                this.configurationData["resp"] = {};
              }

              this.appState.setConfigurationData(this.configurationData);
              // Create a deep copy for SaveSetData to avoid reference issues
              const saveDataCopy = JSON.parse(JSON.stringify(this.configurationData));
              this.appState.setSaveSetData(saveDataCopy);
              this.appState.setSaveSetData1(saveDataCopy);
              this.availFeat.setConfigurationData(
                this.configurationData
              );
              // this.loading = false;
              const mode = this.appState.getStoreConfigMode() || {};
              if (
                mode["storeConfigModeCheck"] &&
                (mode["storeConfigModeData"] === "Standard" ||
                  mode["storeConfigModeData"] === "quickConfig")
              ) {
                this.uiSignals.sendModes("Standard");
              } else {
                this.uiSignals.sendModes("Advanced");
              }
              this.appState.removeDeviceVersion();
              this.readDevice = "readDevice";

              // (Previously: `if (this.corruptbitfailed) { }` — empty branch removed.)
              //  this.activemodal.close();
              this.dimmingInterfaceSvc.sendDimmingCheckValidation("");
              this.appState.setFromDeviceList("true");

              if (this.corruptbitfailed) {
                this.readDevice = "readDevice";
              }
              if(!this.iscorrupt){
                this.route.navigate(["/featureLanding"]);
              }

              this.appState.getConfigurationData();

            },
            (error) => {
              this.loading = false;
              const modalRef = this.modalService.open(
                ErrorComponent,
                {
                  keyboard: false,
                  centered: true,
                  windowClass: "error-style, error-style1",
                }
              );
              modalRef.componentInstance.errorName =
                error["message"];
              modalRef.componentInstance.errorDescription =
                error["description"];
            }
          );
      } else {
        this.activemodal.close();
        this.route.navigate(["/featureNonSupported"]);
      }
    });
  }
}
